import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import {
  LayoutDashboard, Search, Users, Settings, PlusCircle, Bell,
  Trash2, UserPlus, Clock, CheckCircle, XCircle, Calendar,
  Briefcase, Pencil, Zap, BarChart3, Shield, CreditCard, User, X, Upload, FileDown, GraduationCap, ArrowLeft, Inbox,
  Edit2, Building, Mail, MapPin, Award, Sliders, Save, Lock, MessageSquare, FileText, Eye,
  Layers, Building2, BookOpen, UserCheck, Info, UserX, TrendingUp, Code, Brain, RefreshCw, Menu,
  ChevronLeft, ChevronRight, Camera, PieChart as PieChartIcon, AlertCircle
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { BASE_URL } from '../api';
import { FeatureGate, SubFeatureGate } from '../components/FeatureGate';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import CalendarLib from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import DiscoverProfessorsUni from './DiscoverProfessorsUni';
import UniAiReports from '../components/university/UniAiReports';
import './DiscoverProfessorsUni.css';
import './UniDashboard.css';
import ConfirmModal from '../components/ConfirmModal';
import { INDUSTRY_DOMAINS } from '../constants/domains';
import PaymentModal from '../components/PaymentModal';
import { RATE_CARD, calculateTotalHours } from '../utils/engagementUtils';
import BankDetails from '../components/BankDetails';
import OnboardingTour from '../components/OnboardingTour';
import RoleManagement from '../components/RoleManagement';
import CardInfoTooltip from '../components/CardInfoTooltip';
import { LMS_BASE_URL, LMS_JWT_TOKEN, DEFAULT_TENANT_ID } from '../constants/lms';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import enGbLocale from '@fullcalendar/core/locales/en-gb';
import PartnerCourses from '../components/university/PartnerCourses';
import FacultyManagement from '../components/university/FacultyManagement';
import TpoManagement from '../components/university/TpoManagement';
import CustomRoleManagementPage from '../components/CustomRoleManagementPage';
import RoleConfigurationPage from '../components/RoleConfigurationPage';
import UIElementGuard, { clearUIRulesCache } from '../components/UIElementGuard';
import StudentProfileDetail from '../components/StudentProfileDetail';
import DiscussionsPage from './DiscussionsPage';
import ChatPage from './ChatPage';
import PlacementDrivesModule from '../components/PlacementDrivesModule';
import AppliedCandidatesContainer from '../components/university/AppliedCandidatesContainer';
import StageAllocation from '../components/university/StageAllocation';

const UniPlacementOverview = () => {
  const [jobs, setJobs] = useState([]);
  const [hiredCount, setHiredCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeOverviewTab, setActiveOverviewTab] = useState('pending');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobsRes, appsRes] = await Promise.all([
          api.get('/tpo/jobs'),
          api.get('/tpo/applied-candidates')
        ]);
        setJobs(jobsRes.data || []);

        const allApps = appsRes.data || [];
        const hiredApps = allApps.filter(a => a.status && a.status.toUpperCase() === 'HIRED');
        const rejectedApps = allApps.filter(a => a.status && a.status.toUpperCase() === 'REJECTED');
        setHiredCount(hiredApps.length);
        setRejectedCount(rejectedApps.length);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalJobs = jobs.length;
  const pendingJobs = jobs.filter(j => j.status === 'PENDING').length;
  const approvedJobs = jobs.filter(j => j.status === 'APPROVED').length;
  const companiesCount = new Set(jobs.map(j => j.company_name)).size;

  const displayedJobs = activeOverviewTab === 'all' ? jobs : (activeOverviewTab === 'pending' ? jobs.filter(j => j.status === 'PENDING') : jobs.filter(j => j.status === 'APPROVED'));

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '24px', color: 'var(--uni-primary)' }}>Placement Overview</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid var(--uni-border)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ color: 'var(--uni-muted)', fontSize: '0.85rem', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>Total Jobs</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--uni-primary)', marginTop: 'auto' }}>{totalJobs}</div>
        </div>
        <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid var(--uni-border)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ color: 'var(--uni-muted)', fontSize: '0.85rem', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>Pending Jobs</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#f59e0b', marginTop: 'auto' }}>{pendingJobs}</div>
        </div>
        <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid var(--uni-border)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ color: 'var(--uni-muted)', fontSize: '0.85rem', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>Approved Jobs</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#10b981', marginTop: 'auto' }}>{approvedJobs}</div>
        </div>
        <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid var(--uni-border)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ color: 'var(--uni-muted)', fontSize: '0.85rem', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>Corporate Partners</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#3b82f6', marginTop: 'auto' }}>{companiesCount}</div>
        </div>
        <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid var(--uni-border)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ color: 'var(--uni-muted)', fontSize: '0.85rem', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>Hired Students</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#8b5cf6', marginTop: 'auto' }}>{hiredCount}</div>
        </div>
        <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid var(--uni-border)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ color: 'var(--uni-muted)', fontSize: '0.85rem', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>Rejected Students</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#ef4444', marginTop: 'auto' }}>{rejectedCount}</div>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid var(--uni-border)', overflow: 'hidden' }}>
        <div style={{ padding: '0 20px', borderBottom: '1px solid var(--uni-border)', background: '#f8fafc', display: 'flex', gap: '24px' }}>
          <button
            onClick={() => setActiveOverviewTab('all')}
            style={{ padding: '16px 4px', background: 'none', border: 'none', borderBottom: activeOverviewTab === 'all' ? '2px solid var(--uni-primary)' : '2px solid transparent', color: activeOverviewTab === 'all' ? 'var(--uni-primary)' : 'var(--uni-muted)', fontWeight: 600, fontSize: '1.05rem', cursor: 'pointer', outline: 'none' }}
          >
            All Jobs ({totalJobs})
          </button>
          <button
            onClick={() => setActiveOverviewTab('pending')}
            style={{ padding: '16px 4px', background: 'none', border: 'none', borderBottom: activeOverviewTab === 'pending' ? '2px solid #f59e0b' : '2px solid transparent', color: activeOverviewTab === 'pending' ? '#d97706' : 'var(--uni-muted)', fontWeight: 600, fontSize: '1.05rem', cursor: 'pointer', outline: 'none' }}
          >
            Pending Jobs ({pendingJobs})
          </button>
          <button
            onClick={() => setActiveOverviewTab('approved')}
            style={{ padding: '16px 4px', background: 'none', border: 'none', borderBottom: activeOverviewTab === 'approved' ? '2px solid #10b981' : '2px solid transparent', color: activeOverviewTab === 'approved' ? '#10b981' : 'var(--uni-muted)', fontWeight: 600, fontSize: '1.05rem', cursor: 'pointer', outline: 'none' }}
          >
            Approved Jobs ({approvedJobs})
          </button>
        </div>
        <div style={{ padding: '20px' }}>
          {loading ? (
            <div>Loading placement data...</div>
          ) : displayedJobs.length === 0 ? (
            <div style={{ color: 'var(--uni-muted)' }}>No jobs found in this category.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--uni-border)', color: 'var(--uni-muted)', fontSize: '0.85rem', textTransform: 'uppercase' }}>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>Job Role</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>Corporate Partner</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>Location</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>Package</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedJobs.map(job => (
                    <tr key={job.id} style={{ borderBottom: '1px solid var(--uni-border)' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--uni-text)' }}>{job.job_name}</td>
                      <td style={{ padding: '12px 16px', color: 'var(--uni-muted)' }}>{job.company_name ? job.company_name.replace(/\b\w/g, l => l.toUpperCase()) : 'Corporate Partner'}</td>
                      <td style={{ padding: '12px 16px', color: 'var(--uni-muted)' }}>{job.location || 'Not Specified'}</td>
                      <td style={{ padding: '12px 16px', color: 'var(--uni-muted)' }}>{job.salary_package || 'Not Disclosed'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: job.status === 'APPROVED' ? '#dcfce7' : (job.status === 'REJECTED' ? '#fee2e2' : '#fef3c7'),
                          color: job.status === 'APPROVED' ? '#166534' : (job.status === 'REJECTED' ? '#991b1b' : '#92400e'),
                          whiteSpace: 'nowrap'
                        }}>
                          {job.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


function UniDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [myRequirements, setMyRequirements] = useState([]);
  const [, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Close sidebar and switch tab (used by all sidebar nav buttons)
  const navTo = (tab) => {
    setActiveTab(tab);
    setIsSidebarOpen(false);
  };
  const [selectedCandidateCompany, setSelectedCandidateCompany] = useState(null);
  const [selectedCandidateRole, setSelectedCandidateRole] = useState(null);
  const [customRoles, setCustomRoles] = useState([]);
  const [selectedCustomRole, setSelectedCustomRole] = useState(null);
  const [isUserMgmtOpen, setIsUserMgmtOpen] = useState(false);
  const [isDeactivatedOpen, setIsDeactivatedOpen] = useState(false);
  const [permissionError, setPermissionError] = useState('');
  const [accessibleModules, setAccessibleModules] = useState(() => {
    try {
      const saved = localStorage.getItem('accessible_modules');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [showPostModal, setShowPostModal] = useState(false);

  const [newReq, setNewReq] = useState({
    title: '',
    description: '',
    required_domain: INDUSTRY_DOMAINS[0],
    engagement_type: 'MODULAR',
    delivery_mode: 'Remote',
    location: '',
    city: '',
    state: '',
    country: '',
    event_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    duration_value: 1,
    duration_unit: 'Hours',
    weekly_hours: 0,
    monthly_salary: 0,
    budget: 0,
    days_of_week: [],
    course_id: '',
    academic_batch: ''
  });

  const [editingReqId, setEditingReqId] = useState(null);
  const [selectedReqId, setSelectedReqId] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);

  const [myExperts, setMyExperts] = useState([]);
  const [loadingExperts, setLoadingExperts] = useState(false);

  // Profile State
  const [uniProfile, setUniProfile] = useState(null);
  const [dashboardAnalytics, setDashboardAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [studentsBatchChartType, setStudentsBatchChartType] = useState('bar');

  const BATCH_DONUT_COLORS = [
    '#6366f1', '#06b6d4', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#3b82f6', '#14b8a6', '#f97316', '#64748b'
  ];

  const sortedStudentBatches = useMemo(() => {
    if (!dashboardAnalytics?.students_by_batch) return [];
    return [...dashboardAnalytics.students_by_batch].sort((a, b) => {
      const nameA = String(a.name || '').trim();
      const nameB = String(b.name || '').trim();
      if (nameA.toLowerCase() === 'unassigned') return 1;
      if (nameB.toLowerCase() === 'unassigned') return -1;
      return nameA.localeCompare(nameB, undefined, { numeric: true });
    });
  }, [dashboardAnalytics?.students_by_batch]);

  const totalStudentsInBatches = useMemo(() => {
    return sortedStudentBatches.reduce((acc, curr) => acc + (Number(curr.value) || 0), 0);
  }, [sortedStudentBatches]);

  const donutStudentBatches = useMemo(() => {
    if (sortedStudentBatches.length <= 7) return sortedStudentBatches;
    const byVal = [...sortedStudentBatches].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
    const top = byVal.slice(0, 6);
    const otherVal = byVal.slice(6).reduce((acc, curr) => acc + (Number(curr.value) || 0), 0);
    if (otherVal > 0) {
      top.push({ name: `Other (${byVal.length - 6} batches)`, value: otherVal });
    }
    return top;
  }, [sortedStudentBatches]);

  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  // Payment State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // Feedback Modal State
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackData, setFeedbackData] = useState({
    session_id: null,
    session_type: null,
    quality_rating: 0,
    expert_effectiveness: 0,
    relevance_rating: 0,
    comments: ''
  });

  // Confirm Modal State
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: null
  });
  const closeConfirmModal = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  // Settings Edit State
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [editedSettings, setEditedSettings] = useState({});
  const [settingsSubTab, setSettingsSubTab] = useState('profile'); // 'profile', 'bank-details'
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [toasts, setToasts] = useState([]);

  // Refund Request State
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundData, setRefundData] = useState({
    payment_id: null,
    amount: 0,
    professor_name: '',
    session_title: '',
    session_date: '',
    reason: 'INSTITUTIONAL_CANCELLATION',
    details: ''
  });

  // Proposal Modal States (to match DiscoverProfessorsUni)
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [selectedProf, setSelectedProf] = useState(null);
  const [busySlots, setBusySlots] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [proposalForm, setProposalForm] = useState({
    title: '',
    description: '',
    domain: '',
    engagement_type: 'MODULAR',
    delivery_mode: 'Remote',
    event_date: '',
    start_time: '',
    end_time: '',
    duration_value: 1,
    days_of_week: [],
    end_date: '',
    weekly_hours: 0
  });

  // const [notification, setNotification] = useState(null); // { message, type }


  const [lmsCourses, setLmsCourses] = useState([]);
  const [uniAssignedCourses, setUniAssignedCourses] = useState([]);

  const fetchCourses = async () => {
    try {
      const res = await api.get('/courses');
      setLmsCourses(res.data || []);
      const uniRes = await api.get('/integrations/edvergencex/uni-courses');
      setUniAssignedCourses(uniRes.data?.data || []);
    } catch (e) {
      console.error("Failed to fetch courses", e);
    }
  };


  const formatErrorMessage = useCallback((err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (Array.isArray(err)) {
      return err.map(item => formatErrorMessage(item)).filter(Boolean).join('; ');
    }
    if (typeof err === 'object') {
      if (err.msg) return err.msg;
      if (err.detail) return formatErrorMessage(err.detail);
      if (err.message) return formatErrorMessage(err.message);
      try {
        return JSON.stringify(err);
      } catch (e) {
        return 'An error occurred.';
      }
    }
    return String(err);
  }, []);

  // Toast notification helper
  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now() + Math.random();
    const formattedMsg = formatErrorMessage(message);
    setToasts(prev => [...prev, { id, message: formattedMsg, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 4000);
  }, [formatErrorMessage]);

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };




  // Unread Messages State
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  // Fetch Notifications & Messages
  const fetchNotifs = async () => {
    try {
      const res = await api.get('/compliance/notifications');
      setNotifications(res.data);
    } catch (e) {
      console.error("Failed to fetch notifications", e);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await api.post(`/compliance/notifications/${id}/mark-read`);
      setNotifications(notifications.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (e) {
      console.error("Failed to mark notification as read", e);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.post('/compliance/notifications/mark-all-read');
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
    } catch (e) {
      console.error("Failed to mark all notifications as read", e);
    }
  };

  const fetchUnreadMessages = async () => {
    try {
      const res = await api.get('/chat/conversations');
      const totalUnread = res.data.reduce((acc, curr) => acc + (curr.unread_count || 0), 0);
      setUnreadMessagesCount(totalUnread);
    } catch (err) {
      console.error("Failed to fetch unread messages", err);
    }
  };

  useEffect(() => {
    fetchProfile(); // Fetch profile immediately to get Tier
    fetchAnalytics(); // Fetch dashboard analytics
    fetchAccessibleModules();
    fetchNotifs();
    fetchUnreadMessages();
    fetchCourses();
    const interval = setInterval(fetchUnreadMessages, 60000);
    return () => clearInterval(interval);
  }, []);



  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    } else if (location.state?.showSettings || location.state?.showProfile) {
      setActiveTab('settings');
      // Clear state so it doesn't keep switching back on every re-render
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.showDiscussions) {
      setActiveTab('discussions');
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.showChat) {
      setActiveTab('chat');
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.showStageAllocation) {
      setActiveTab('stage_allocation');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.search, navigate, location.pathname]);

  // Enrolled Students Modal State
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [currentStudentsList, setCurrentStudentsList] = useState([]);
  const [currentReqTitle, setCurrentReqTitle] = useState('');

  // My Experts Toggle
  const [showExpertsToggle, setShowExpertsToggle] = useState(false);

  // Students Section State
  const [students, setStudents] = useState([]);

  const [editStudentModal, setEditStudentModal] = useState({ show: false, student: null });
  const [editStudentForm, setEditStudentForm] = useState({
    full_name: '', phone_number: '', student_date_of_birth: '', gender: '', usn: '', graduation_level: '', program: '', academic_year: '', academic_batch: '', admission_semester: '', branch: ''
  });
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [studentCurrentPage, setStudentCurrentPage] = useState(1);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadStep, setUploadStep] = useState('select'); // 'select', 'preview', 'result'
  const [uploading, setUploading] = useState(false);
  const [showFullPreviewModal, setShowFullPreviewModal] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState(null);

  const [selectedStudentProfile, setSelectedStudentProfile] = useState(null);
  const [studentCourses, setStudentCourses] = useState([]);
  const [loadingStudentCourses, setLoadingStudentCourses] = useState(false);

  const handleViewStudentProfile = async (student) => {
    setSelectedStudentProfile(student);
    const studentId = student.user_id || student.id;
    if (studentId) {
      try {
        setLoadingStudentCourses(true);
        const res = await api.get(`/faculty/students/${studentId}/courses-and-marks`);
        setStudentCourses(res.data);
      } catch (err) {
        console.error('Error fetching student courses:', err);
        setStudentCourses([]);
      } finally {
        setLoadingStudentCourses(false);
      }
    } else {
      setStudentCourses([]);
    }
  };

  const openStudentsModal = (students, title) => {
    setCurrentStudentsList(students || []);
    setCurrentReqTitle(title);
    setShowStudentsModal(true);
  };

  const stats = useMemo(() => {
    const total = myRequirements.length;
    const open = myRequirements.filter(r => r.status === 'OPEN' || r.status === 'PENDING').length;
    const confirmed = myRequirements.filter(r => r.status === 'ACCEPTED').length;
    return { total, open, confirmed };
  }, [myRequirements]);

  // const COLORS = ['#4f46e5', '#06b6d4', '#10b981', '#f59e0b'];
  // const chartData = [
  //   { name: 'AI', value: 400 }, { name: 'Fintech', value: 300 },
  //   { name: 'Law', value: 200 }, { name: 'Marketing', value: 278 },
  // ];

  useEffect(() => {
    fetchCustomRoles();
  }, []);

  useEffect(() => {
    if (activeTab === 'schedule' || activeTab === 'dashboard') {
      fetchRequirements();
    } else if (activeTab === 'completed') {
      fetchRequirements();
      fetchExperts();
    } else if (activeTab === 'settings') {
      fetchProfile();
    } else if (activeTab === 'students') {
      fetchStudents('active');
    } else if (activeTab === 'deactivated_students') {
      fetchStudents('deactivated');
    } else if (activeTab === 'companies') {
      fetchCompanies();
    }

    // Automatically open User Management if one of its tabs is active
    if (['students', 'faculty', 'tpo'].includes(activeTab) || (activeTab && activeTab.startsWith('custom_role_'))) {
      setIsUserMgmtOpen(true);
      if (activeTab && activeTab.startsWith('custom_role_')) {
        const roleIdStr = activeTab.replace('custom_role_', '');
        const found = customRoles.find(r => String(r.id) === roleIdStr);
        if (found) setSelectedCustomRole(found);
      }
    }

    // Automatically open Deactivated section if one of its tabs is active
    if (['deactivated_students', 'deactivated_faculty', 'deactivated_tpo'].includes(activeTab) || (activeTab && activeTab.startsWith('deactivated_custom_role_'))) {
      setIsDeactivatedOpen(true);
      if (activeTab && activeTab.startsWith('deactivated_custom_role_')) {
        const roleIdStr = activeTab.replace('deactivated_custom_role_', '');
        const found = customRoles.find(r => String(r.id) === roleIdStr);
        if (found) setSelectedCustomRole(found);
      }
    }
  }, [activeTab, customRoles]);

  useEffect(() => {
    if (isUserMgmtOpen || isDeactivatedOpen) {
      fetchCustomRoles();
    }
  }, [isUserMgmtOpen, isDeactivatedOpen]);

  const fetchCustomRoles = async () => {
    try {
      const res = await api.get('/university/roles');
      setCustomRoles(res.data || []);
    } catch (err) {
      console.error("Failed to fetch custom roles", err);
    }
  };

  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const res = await api.get('/university/analytics');
      setDashboardAnalytics(res.data);
    } catch (err) {
      console.error("Failed to fetch analytics", err);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await api.get('/profile/me');
      setUniProfile(res.data);
      if (res.data?.accessible_modules) {
        setAccessibleModules(res.data.accessible_modules);
        localStorage.setItem('accessible_modules', JSON.stringify(res.data.accessible_modules));
      }
    } catch (err) {
      console.error("Failed to fetch profile", err);
    }
  };

  const fetchAccessibleModules = async () => {
    try {
      const res = await api.get('/auth/accessible-modules');
      if (res.data?.modules) {
        setAccessibleModules(res.data.modules);
        localStorage.setItem('accessible_modules', JSON.stringify(res.data.modules));
      }
    } catch (err) {
      console.error("Failed to fetch accessible modules", err);
    }
  };

  const handleSaveSettings = async () => {
    // Validations
    if (editedSettings.website_url) {
      const urlLower = editedSettings.website_url.toLowerCase();
      if (!urlLower.endsWith('.edu') && !urlLower.endsWith('.edu.in')) {
        showToast('Website URL must end with .edu or .edu.in', 'error');
        return;
      }
    }

    if (editedSettings.establishment_year && editedSettings.establishment_year > new Date().getFullYear()) {
      showToast('Establishment Year cannot be in the future', 'error');
      return;
    }

    if (editedSettings.contact_email) {
      const emailLower = editedSettings.contact_email.toLowerCase();
      if (!emailLower.endsWith('.edu') && !emailLower.endsWith('.edu.in')) {
        showToast('Contact Email must end with .edu or .edu.in', 'error');
        return;
      }
    }

    if (editedSettings.postal_code) {
      const zipStr = String(editedSettings.postal_code).trim();
      if (!/^\d{1,6}$/.test(zipStr) || zipStr.length > 6) {
        showToast('Postal / ZIP Code must be up to 6 digits', 'error');
        return;
      }
    }

    if (editedSettings.student_strength !== undefined && editedSettings.student_strength !== null && parseInt(editedSettings.student_strength) < 0) {
      showToast('Student Strength cannot be negative', 'error');
      return;
    }

    try {
      const res = await api.put('/profile/update', editedSettings);
      setUniProfile(res.data);
      setIsEditingSettings(false);
      setEditedSettings({});
      showToast('Profile updated successfully!', 'success');
      // Refresh profile to get updated completion percentage
      fetchProfile();
    } catch (err) {
      console.error("Failed to update profile", err);
      showToast('Failed to update profile. Please try again.', 'error');
    }
  };

  const getLogoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url;
    const base = process.env.REACT_APP_API_URL || '';
    return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('Please upload a valid image file (PNG, JPG, WebP, SVG)', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('Logo file size must be less than 5MB', 'error');
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/upload-logo', formData);
      showToast('Institutional logo updated successfully!', 'success');
      setUniProfile(prev => ({ ...prev, logo_url: res.data.logo_url }));
      if (isEditingSettings) {
        setEditedSettings(prev => ({ ...prev, logo_url: res.data.logo_url }));
      }
      fetchProfile();
    } catch (err) {
      console.error('Failed to upload logo:', err);
      showToast('Upload failed: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Remove Institutional Logo',
      message: 'Are you sure you want to remove the institutional logo? The default monogram will be displayed instead.',
      type: 'danger',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await api.delete('/delete-logo');
          showToast('Institutional logo removed', 'success');
          setUniProfile(prev => ({ ...prev, logo_url: null }));
          if (isEditingSettings) {
            setEditedSettings(prev => ({ ...prev, logo_url: null }));
          }
          fetchProfile();
        } catch (err) {
          console.error('Failed to delete logo:', err);
          showToast('Failed to remove logo', 'error');
        }
      }
    });
  };

  const handleRequestRefund = (req) => {
    setRefundData({
      payment_id: req.payment_id,
      amount: req.amount || req.budget || 0,
      professor_name: req.professor_name || 'Assigned Expert',
      session_title: req.title || 'Session Details',
      session_date: req.event_date ? new Date(req.event_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A',
      reason: 'INSTITUTIONAL_CANCELLATION',
      details: ''
    });
    setShowRefundModal(true);
  };

  const submitRefundRequest = async () => {
    try {
      if (!refundData.payment_id) {
        showToast("Error: Missing payment ID", "error");
        return;
      }
      if (refundData.details.length < 20) {
        showToast("Reason must be at least 20 characters long", "error");
        return;
      }
      await api.post('/refunds/request', refundData);
      showToast("Refund request submitted successfully!", "success");
      setShowRefundModal(false);
      fetchRequirements();
    } catch (err) {
      showToast(err.response?.data?.detail || "Refund request failed.", 'error');
    }
  };

  const handleChangePassword = async () => {
    // Validate inputs
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      showToast('Please fill in all password fields', 'error');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast('New passwords do not match', 'error');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showToast('New password must be at least 6 characters long', 'error');
      return;
    }

    try {
      await api.post('/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });

      showToast('Password changed successfully!', 'success');
      setShowChangePasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to change password', 'error');
    }
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const res = await api.get('/university/companies');
      setCompanies(res.data);
    } catch (err) {
      console.error("Failed to fetch companies", err);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchStudents = async (statusParam = 'active') => {
    setLoadingStudents(true);
    setPermissionError('');
    try {
      const res = await api.get(`/students?status=${statusParam}`);
      setStudents(res.data || []);
    } catch (err) {
      console.error("Failed to fetch students", err);
      if (err.response && err.response.status === 403) {
        setPermissionError(err.response?.data?.detail || "You don't have permission to access this resource.");
      }
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleToggleStudentStatus = async (student) => {
    const newActive = !student.is_active;
    const newStatus = newActive ? 'Active' : 'Deactivated';
    const actionText = newActive ? 'activate' : 'deactivate';

    setConfirmModal({
      isOpen: true,
      title: `Confirm ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`,
      message: `Are you sure you want to ${actionText} ${student.full_name || 'this student'}?`,
      type: newActive ? 'primary' : 'danger',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await api.put(`/university/users/${student.id}/status`, {
            is_active: newActive,
            status: newStatus
          });
          fetchStudents(activeTab === 'deactivated_students' ? 'deactivated' : 'active');
          showToast(`Student account ${actionText}d successfully.`, 'success');
        } catch (err) {
          console.error("Error updating student status:", err);
          showToast("Failed to update student status", "error");
        }
      }
    });
  };



  const fetchExperts = async () => {
    setLoadingExperts(true);
    try {
      const res = await api.get('/my-experts');
      setMyExperts(res.data);
    } catch (err) {
      console.error("Failed to fetch experts", err);
    } finally {
      setLoadingExperts(false);
    }
  };

  // Proposal Modal Handlers (from DiscoverProfessorsUni)
  const calculateProposalBudget = () => {
    const currentTier = uniProfile?.tier?.trim();
    const rateInfo = RATE_CARD[proposalForm.engagement_type];
    const hourlyRate = (rateInfo && currentTier) ? (rateInfo[currentTier] || 0) : 0;
    const totalHours = calculateTotalHours(proposalForm);
    return Math.round(hourlyRate * totalHours);
  };

  const handleOpenProposalModal = async (prof) => {
    setSelectedProf(prof);
    setShowProposalModal(true);
    setLoadingCalendar(true);
    // Initialize form with professor's domain
    setProposalForm(prev => ({
      ...prev,
      title: '',
      description: '',
      domain: prof.domain || "",
      engagement_type: 'MODULAR',
      delivery_mode: 'Remote',
      event_date: '',
      start_time: '',
      end_time: '',
      duration_value: 1,
      days_of_week: [],
      end_date: '',
      weekly_hours: 0
    }));
    setBusySlots([]);
    setAvailability([]);

    try {
      const busyRes = await api.get(`/professor/${prof.id}/busy-slots`);
      setBusySlots(busyRes.data);

      let availData = [];
      try {
        const res = await api.get(`/professor/${prof.id}/availability`);
        availData = res.data;
      } catch (e1) {
        try {
          const res = await api.get(`/professors/${prof.id}/availability`);
          availData = res.data;
        } catch (e2) {
          try {
            const profileRes = await api.get(`/professors/${prof.id}`);
            if (profileRes.data && profileRes.data.availability) availData = profileRes.data.availability;
          } catch (e3) {
            console.warn("All strategies failed");
          }
        }
      }
      if (!Array.isArray(availData)) availData = [];
      setAvailability(availData);
    } catch (err) {
      console.error("Calendar fetch error:", err);
    } finally {
      setLoadingCalendar(false);
    }
  };

  const handleProposeSubmit = async (e) => {
    e.preventDefault();
    const isLongTerm = ['ADJUNCT', 'FRACTIONAL', 'MODULAR'].includes(proposalForm.engagement_type);

    if (isLongTerm) {
      if (!proposalForm.event_date || !proposalForm.end_date) {
        showToast("Please select both Start Date and End Date for this engagement.", 'error');
        return;
      }
      if (['ADJUNCT', 'MODULAR'].includes(proposalForm.engagement_type)) {
        if (!proposalForm.start_time || !proposalForm.end_time) {
          showToast("Please specify both Start Time and End Time.", 'error');
          return;
        }
        if (proposalForm.start_time >= proposalForm.end_time) {
          showToast("End Time must be after Start Time.", 'error');
          return;
        }
      }
      if (proposalForm.engagement_type === 'ADJUNCT' && proposalForm.days_of_week.length === 0) {
        showToast("Please select at least one day of the week.", 'error');
        return;
      }
    } else {
      if (!proposalForm.event_date || !proposalForm.start_time) {
        showToast("Please select a specific time slot on the calendar.", 'error');
        return;
      }
    }

    const selectedDateTime = new Date(`${proposalForm.event_date}T${proposalForm.start_time}`);
    if (selectedDateTime < new Date()) {
      showToast("You cannot schedule a session in the past.", 'error');
      return;
    }

    try {
      const totalHours = calculateTotalHours(proposalForm);
      const budget = calculateProposalBudget();

      const payload = {
        title: proposalForm.title,
        description: proposalForm.description,
        required_domain: proposalForm.domain,
        engagement_type: proposalForm.engagement_type,
        delivery_mode: proposalForm.delivery_mode,
        event_date: proposalForm.event_date || null,
        end_date: proposalForm.end_date || null,
        start_time: proposalForm.start_time || null,
        end_time: proposalForm.end_time || null,
        days_of_week: proposalForm.days_of_week,
        weekly_hours: proposalForm.weekly_hours,
        duration_value: proposalForm.duration_value,
        duration_unit: "Hours",
        budget: budget,
        total_estimated_hours: totalHours
      };

      await api.post(`/university/propose-custom-session/${selectedProf.id}`, payload);
      showToast(`Proposal for "${proposalForm.title}" sent to ${selectedProf.full_name}!`, 'success');
      setShowProposalModal(false);
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.detail || "Failed to send proposal.", 'error');
    }
  };

  const calendarEvents = useMemo(() => {
    const events = [];
    availability.forEach(slot => {
      const getDayNumber = (day) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return days.indexOf(day);
      };
      if (slot.day_of_week && !slot.specific_date) {
        events.push({
          groupId: 'availableForSession',
          daysOfWeek: [getDayNumber(slot.day_of_week)],
          startTime: slot.start_time,
          endTime: slot.end_time,
          display: 'background',
          color: '#58e78aff'
        });
      } else if (slot.specific_date) {
        let dateOnly = slot.specific_date;
        if (dateOnly.includes('T')) dateOnly = dateOnly.split('T')[0];
        events.push({
          groupId: 'availableForSession',
          start: `${dateOnly}T${slot.start_time}`,
          end: `${dateOnly}T${slot.end_time}`,
          display: 'background',
          color: '#6dea99'
        });
      }
    });

    const formattedBusy = busySlots.map(slot => ({
      ...slot,
      title: slot.title || 'Occupied',
      display: 'block',
      backgroundColor: 'var(--border)',
      borderColor: '#cbd5e0',
      textColor: '#718096',
      editable: false,
      overlap: false,
    }));
    events.push(...formattedBusy);

    if (proposalForm.start_time && proposalForm.end_time) {
      if (proposalForm.engagement_type === 'ADJUNCT' && proposalForm.days_of_week.length > 0) {
        const daysMap = { 'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4, 'Friday': 5, 'Saturday': 6 };
        const selectedDays = proposalForm.days_of_week.map(d => daysMap[d]);
        events.push({
          id: 'university-selection-recurring',
          title: 'Your Proposal',
          daysOfWeek: selectedDays,
          startTime: proposalForm.start_time,
          endTime: proposalForm.end_time,
          backgroundColor: '#4f46e5',
          borderColor: '#4338ca',
          textColor: '#ffffff'
        });
      } else if (proposalForm.engagement_type === 'MODULAR' && proposalForm.event_date && proposalForm.end_date) {
        const endDate = new Date(proposalForm.end_date);
        endDate.setDate(endDate.getDate() + 1);
        const endRecurStr = endDate.toISOString().split('T')[0];
        events.push({
          id: 'university-selection-modular',
          title: 'Proposed Module',
          daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
          startTime: proposalForm.start_time,
          endTime: proposalForm.end_time,
          startRecur: proposalForm.event_date,
          endRecur: endRecurStr,
          backgroundColor: '#4f46e5',
          borderColor: '#4338ca',
          textColor: '#ffffff'
        });
      } else if (proposalForm.event_date) {
        events.push({
          id: 'university-selection',
          title: 'Proposed Start',
          start: `${proposalForm.event_date}T${proposalForm.start_time}`,
          end: `${proposalForm.event_date}T${proposalForm.end_time}`,
          backgroundColor: '#4f46e5',
          borderColor: '#4338ca',
          textColor: '#ffffff'
        });
      }
    }
    return events;
  }, [busySlots, proposalForm, availability]);


  const fetchRequirements = async () => {
    try {
      const res = await api.get('/my-requirements');
      console.log("University Requirements Response:", res.data);
      // Log enrolled students for each requirement
      res.data.forEach(req => {
        if (req.enrolled_students) {
          console.log(`Requirement "${req.title}" has ${req.enrolled_students.length} enrolled students:`, req.enrolled_students);
        }
      });
      setMyRequirements(res.data || []);
    } catch (err) {
      console.error("Fetch Requirements Error:", err);
    }
    finally {
      setLoading(false);
    }
  };

  // State for all sessions (for calendar display)
  const [allSessions, setAllSessions] = useState([]);

  // Fetch all PublicSessions for accepted requirements
  const fetchAllSessions = async () => {
    try {
      // Get all accepted requirements
      const acceptedReqs = myRequirements.filter(r => r.status?.toUpperCase() === 'ACCEPTED');

      if (acceptedReqs.length === 0) {
        setAllSessions([]);
        return;
      }

      // Fetch all PublicSessions for each accepted requirement
      const sessionsPromises = acceptedReqs.map(async (req) => {
        try {
          const res = await api.get(`/public-sessions?requirement_id=${req.id}`);
          return res.data.map(session => ({
            ...session,
            requirement_title: req.title,
            requirement_id: req.id,
            professor_name: req.professor_name,
            engagement_type: req.engagement_type
          }));
        } catch (err) {
          console.error(`Failed to fetch sessions for requirement ${req.id}:`, err);
          return [];
        }
      });

      const sessionsArrays = await Promise.all(sessionsPromises);
      const flattenedSessions = sessionsArrays.flat();

      console.log(`Fetched ${flattenedSessions.length} total sessions for ${acceptedReqs.length} accepted requirements`);
      setAllSessions(flattenedSessions);
    } catch (err) {
      console.error("Error fetching all sessions:", err);
    }
  };

  // Update fetchRequirements to also fetch sessions
  useEffect(() => {
    if (myRequirements.length > 0) {
      fetchAllSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myRequirements]);

  const getSessionsForDate = (date) => {
    const dStr = date.toISOString().split('T')[0];
    // Use allSessions instead of myRequirements
    return allSessions.filter(session =>
      session.event_date?.split('T')[0] === dStr
    );
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    // Calculate Budget based on Tiered Rate Card
    // Calculate Budget based on Tiered Rate Card
    const currentTier = uniProfile?.tier?.trim();
    const rateInfo = RATE_CARD[newReq.engagement_type];
    const hourlyRate = (rateInfo && currentTier) ? (rateInfo[currentTier] || 0) : 0;

    const totalHours = calculateTotalHours(newReq);
    const calculatedBudget = Math.round(hourlyRate * totalHours);

    // Construct base payload
    let finalData = {
      title: newReq.title,
      description: newReq.description,
      required_domain: newReq.required_domain || INDUSTRY_DOMAINS[0],
      engagement_type: newReq.engagement_type,
      delivery_mode: newReq.delivery_mode,
      location: (newReq.city && newReq.country) ? `${newReq.city}, ${newReq.state ? newReq.state + ', ' : ''}${newReq.country}` : null,
      city: newReq.city,
      state: newReq.state,
      country: newReq.country,
      budget: calculatedBudget,
      total_estimated_hours: totalHours,
      monthly_salary: 0,
      session_price: 0,
      course_id: newReq.course_id || null,
      academic_batch: newReq.academic_batch || null,
      status: "OPEN"
    };

    // Filter fields based on Engagement Type
    if (newReq.engagement_type === 'VISITING') {
      finalData = {
        ...finalData,
        event_date: newReq.event_date || null,
        start_time: newReq.start_time || null,
        duration_value: newReq.duration_value || 1,
        duration_unit: newReq.duration_unit || 'Hours',
        // Nullify others
        end_date: null,
        end_time: null,
        days_of_week: [],
        weekly_hours: 0
      };
    } else if (newReq.engagement_type === 'FRACTIONAL') {
      finalData = {
        ...finalData,
        event_date: newReq.event_date || null, // Start Date
        end_date: newReq.end_date || null,     // End Date
        weekly_hours: newReq.weekly_hours || 0,
        days_of_week: newReq.days_of_week || [],
        // Nullify others
        start_time: null,
        end_time: null,
        duration_value: null,
        duration_unit: null
      };
    } else if (newReq.engagement_type === 'MODULAR') {
      finalData = {
        ...finalData,
        event_date: newReq.event_date || null, // Module Start Date
        end_date: newReq.end_date || null,     // Module End Date
        start_time: newReq.start_time || null,
        end_time: newReq.end_time || null,
        // Nullify others
        weekly_hours: 0,
        days_of_week: [],
        duration_value: null,
        duration_unit: null
      };
    } else if (newReq.engagement_type === 'ADJUNCT') {
      finalData = {
        ...finalData,
        event_date: newReq.event_date || null,
        end_date: newReq.end_date || null,
        start_time: newReq.start_time || null,
        end_time: newReq.end_time || null,
        days_of_week: newReq.days_of_week || [],
        // Nullify others
        weekly_hours: 0,
        duration_value: null,
        duration_unit: null
      };
    }

    // Clean up empty strings or zero values if needed
    if (!finalData.event_date) delete finalData.event_date;

    try {
      if (editingReqId) {
        await api.put(`/requirements/${editingReqId}`, finalData);
        showToast("Requirement updated successfully!", 'success');
      } else {
        await api.post('/requirements', finalData);
        showToast("Requirement published successfully!", 'success');
      }
      resetForm();
      setShowPostModal(false);
      fetchRequirements();
    } catch (err) {
      console.error("Save error:", err);
      showToast("Error saving requirement: " + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const resetForm = () => {
    setNewReq({
      title: '',
      description: '',
      required_domain: INDUSTRY_DOMAINS[0],
      engagement_type: 'MODULAR',
      delivery_mode: 'Remote',
      location: '',
      city: '',
      state: '',
      country: '',
      event_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      duration_value: 1,
      duration_unit: 'Hours',
      weekly_hours: 0,
      monthly_salary: 0,
      budget: 0,
      days_of_week: [],
      course_id: ''
    });
    setEditingReqId(null);
  };

  const handleDayToggle = (day) => {
    const currentDays = newReq.days_of_week || [];
    if (currentDays.includes(day)) {
      setNewReq({ ...newReq, days_of_week: currentDays.filter(d => d !== day) });
    } else {
      setNewReq({ ...newReq, days_of_week: [...currentDays, day] });
    }
  };

  const viewMatches = async (reqId) => {
    if (selectedReqId === reqId) {
      setSelectedReqId(null);
      return;
    }
    setSelectedReqId(reqId);
    setMatches([]);
    setLoadingMatches(true);
    try {
      const res = await api.get(`/matches/${reqId}`);
      let matchData = res.data.matches || [];
      matchData.sort((a, b) => {
        const score = { 'PROPOSED_BY_POP': 1, 'ACCEPTED': 2, 'REQUESTED': 3, null: 4 };
        return (score[a.invitation_status] || 5) - (score[b.invitation_status] || 5);
      });
      setMatches(matchData);
    } catch (err) {
      setMatches([]);
    }
    finally {
      setLoadingMatches(false);
    }
  };

  const handleSendRequest = async (reqId, popId) => {
    try {
      await api.post(`/${reqId}/engage/${popId}`);
      showToast("Invitation sent to Professor!", 'success');
      viewMatches(reqId);
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to send request.", 'error');
    }
  };

  const handleEngagementResponse = async (engId, status) => {
    setConfirmModal({
      show: true,
      message: `Confirm ${status.toLowerCase()}?`,
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await api.put(`/engagements/${engId}/respond?status=${status}`);
          showToast(`Status updated to ${status} successfully!`, 'success');
          // Refresh requirements to move them between tabs
          await fetchRequirements();
          setSelectedReqId(null);
        } catch (err) {
          showToast("Failed to update status.", 'error');
        }
      }
    });
  };

  const handleGiveFeedback = async (session_id) => {
    // Open feedback modal
    setFeedbackData({
      session_id,
      session_type: 'REQUIREMENT',
      quality_rating: 0,
      expert_effectiveness: 0,
      relevance_rating: 0,
      comments: ''
    });
    setShowFeedbackModal(true);
  };

  const submitFeedback = async () => {
    // Validate required fields
    if (feedbackData.quality_rating === 0) {
      showToast("Please provide a quality rating", 'error');
      return;
    }

    try {
      // Submit feedback
      await api.post('/feedback/submit', {
        session_id: feedbackData.session_id,
        session_type: feedbackData.session_type,
        quality_rating: feedbackData.quality_rating,
        expert_effectiveness: feedbackData.expert_effectiveness || null,
        relevance_rating: feedbackData.relevance_rating || null,
        comments: feedbackData.comments || null
      });

      showToast("Feedback submitted successfully!", 'success');
      setShowFeedbackModal(false);
      fetchRequirements();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to submit feedback", 'error');
    }
  };

  const handleInitPayment = (req) => {
    // Use the professor's accepted price (proposed_amount), fallback to budget if not set
    const paymentAmount = req.proposed_amount || req.budget || 0;

    setPaymentData({
      session: {
        id: req.id,
        title: req.title,
        price: paymentAmount
      },
      sessionType: 'REQUIREMENT'
    });
    setShowPaymentModal(true);
  };

  const handleDelete = async (id) => {
    setConfirmModal({
      show: true,
      message: "Delete this posting?",
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await api.delete(`/requirements/${id}`);
          setMyRequirements(myRequirements.filter(r => r.id !== id));
          showToast("Requirement deleted successfully", 'success');
        } catch (err) {
          showToast("Delete failed.", 'error');
        }
      }
    });
  };

  const handleEditClick = (req) => {
    setNewReq({
      title: req.title,
      description: req.description || '',
      required_domain: req.required_domain,
      engagement_type: req.engagement_type,
      delivery_mode: req.delivery_mode,
      location: req.location || '',
      city: req.city || '',
      state: req.state || '',
      country: req.country || '',
      event_date: req.event_date ? req.event_date.split('T')[0] : '',
      end_date: req.end_date ? req.end_date.split('T')[0] : '',
      start_time: req.start_time || '',
      end_time: req.end_time || '',
      duration_value: req.duration_value || 1,
      duration_unit: req.duration_unit || 'Hours',
      weekly_hours: req.weekly_hours || 0,
      monthly_salary: req.monthly_salary || 0,
      budget: req.budget || 0,
      days_of_week: req.days_of_week || []
    });
    setEditingReqId(req.id);
    setShowPostModal(true);
  };

  // --- TAB: MY SCHEDULE ---
  const renderSchedule = () => {
    const scheduledSessions = myRequirements.filter(r => r.status && r.status.toUpperCase() === 'ACCEPTED');

    return (
      <div className="unidash-fade-in">
        <div className="chart-header" style={{ marginBottom: '20px' }}>
          <Calendar size={18} /> <span>Confirmed Engagement Schedule</span>
        </div>

        {scheduledSessions.length === 0 ? (
          <div className="uni-card empty-state" style={{ textAlign: 'center', padding: '60px' }}>
            <Calendar size={48} style={{ color: 'var(--border)', marginBottom: '16px' }} />
            <p>No confirmed expert sessions in your schedule.</p>
            <small>Finalize a professor's acceptance to see engagements here.</small>
          </div>
        ) : (
          <div className="unidash-schedule-grid">
            {scheduledSessions.map((req) => (
              <div key={req.id} className="uni-schedule-card">
                <div className="schedule-card-top">
                  <h3 className="schedule-card-title">{req.title}</h3>
                  <span className="mode-badge">
                    {req.delivery_mode}
                    {(req.delivery_mode === 'In-Person' || req.delivery_mode === 'Hybrid') && req.location ? ` (${req.location})` : ''}
                  </span>
                </div>

                <div className="schedule-card-subtext">
                  <strong>{req.engagement_type}</strong> • {req.required_domain}
                </div>

                <div className="expert-confirmed-pill">
                  <div className="expert-avatar-circle">
                    {req.professor_name?.charAt(0) || 'P'}
                  </div>
                  <div className="expert-info-text">
                    <span className="name">{req.professor_name || 'Expert Confirmed'}</span>
                    <span className="label">Engaged Faculty</span>
                  </div>
                  <CheckCircle size={18} style={{ marginLeft: 'auto', color: '#10b981' }} />
                </div>

                <div className="schedule-card-footer" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                  <div className="schedule-meta-item">
                    <Calendar size={14} />
                    <span>
                      {formatDate(req.event_date)}
                      {req.end_date && ` - ${formatDate(req.end_date)}`}
                    </span>
                  </div>
                  <div className="schedule-meta-item">
                    <Clock size={14} />
                    <span>
                      {req.engagement_type === 'FRACTIONAL' ? (
                        <span style={{ color: 'var(--text-muted)', fontWeight: '600' }}>
                          {req.weekly_hours ? `${req.weekly_hours} Hrs/Week` : 'Hours TBD'}
                        </span>
                      ) : (
                        <>
                          {req.start_time || 'TBD'}
                          {req.end_time && ` - ${req.end_time}`}
                          {req.days_of_week && req.days_of_week.length > 0 && (
                            <span style={{ marginLeft: '10px', color: 'var(--cyan)', fontWeight: '500' }}>
                              ({req.days_of_week.map(d => d.substring(0, 3)).join(', ')})
                            </span>
                          )}
                          {/* For Visiting types, always show duration if available */}
                          {req.engagement_type === 'VISITING' && req.duration_value && (
                            <span style={{ marginLeft: '12px', color: 'var(--text-muted)' }}>
                              [{req.duration_value} {req.duration_unit}]
                            </span>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                </div>


                {/* Payment Section for University */}
                <div style={{ marginTop: '15px', borderTop: '1px solid var(--border)', paddingTop: '15px' }}>
                  {(!req.payment_status || req.payment_status === 'PENDING') ? (
                    ((req.proposed_amount && req.proposed_amount > 0) || (req.budget && req.budget > 0)) ? (
                      <button
                        className="uni-btn-primary"
                        style={{ width: '100%', justifyContent: 'center' }}
                        onClick={() => handleInitPayment(req)}
                      >
                        <CreditCard size={16} /> Proceed to Payment
                      </button>
                    ) : (
                      <div className="uni-banner warning">
                        ⚠️ Engagement fee not set - Contact professor
                      </div>
                    )
                  ) : (req.payment_status === 'PAID' || req.payment_status === 'SUCCESS') ? (
                    <div className="uni-banner info">
                      ⏳ Payment Processing - Awaiting Confirmation
                    </div>
                  ) : (
                    <div className="payment-status-stack">
                      <div className="payment-escrowed-status">
                        <CheckCircle size={18} /> Payment Escrowed
                      </div>
                      {(req.payment_status === 'HELD' || req.payment_status === 'ENROLLED') && req.payment_id && (
                        <button
                          className="uni-btn-outline sm"
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            borderColor: '#ef4444',
                            color: '#ef4444',
                            fontSize: '12px',
                            marginTop: '8px'
                          }}
                          onClick={() => handleRequestRefund(req)}
                        >
                          <ArrowLeft size={14} />
                          Request Refund
                        </button>
                      )}
                    </div>
                  )}
                </div>



                {/* Student Roster Button */
                  req.enrolled_students && (
                    <div style={{ marginTop: '15px', borderTop: '1px solid var(--border)', paddingTop: '15px' }}>
                      <button
                        className="uni-btn-secondary"
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          padding: '10px',
                          background: 'var(--graphite-dark)',
                          border: '1px solid var(--border)',
                          color: 'var(--text-muted)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => openStudentsModal(req.enrolled_students, req.title)}
                        onMouseEnter={(e) => { e.target.style.background = '#f8fafc'; e.target.style.borderColor = 'var(--text-faint)'; }}
                        onMouseLeave={(e) => { e.target.style.background = 'white'; e.target.style.borderColor = 'var(--border)'; }}
                      >
                        <Users size={16} />
                        View Enrolled Students
                        <span style={{
                          background: 'var(--border)',
                          color: 'var(--text-muted)',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          marginLeft: '4px'
                        }}>
                          {req.student_count || req.enrolled_students.length}
                        </span>
                      </button>
                    </div>
                  )}

              </div>
            ))}
          </div>
        )}
      </div>
    );
  };


  // --- TAB: COMPLETED SESSIONS ---
  const renderCompletedSessions = () => {
    const completedSessions = myRequirements.filter(r => r.status && r.status.toUpperCase() === 'COMPLETED');

    return (
      <div className="unidash-fade-in">
        <div className="chart-header" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <CheckCircle size={18} /> <span>Completed Sessions History</span>
          </div>
          <button
            className="uni-btn-secondary"
            onClick={() => setShowExpertsToggle(!showExpertsToggle)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: 'var(--graphite-dark)',
              border: '1px solid var(--border)',
              color: 'var(--text-muted)',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <Users size={16} />
            {showExpertsToggle ? 'Hide My Experts' : 'Show My Experts'}
          </button>
        </div>

        {/* Integrated Experts Section */}
        {showExpertsToggle && (
          <div style={{ marginBottom: '40px', paddingBottom: '20px', borderBottom: '2px dashed var(--border)' }}>
            {renderMyExperts()}
          </div>
        )}

        {completedSessions.length === 0 ? (
          <div className="uni-card empty-state" style={{ textAlign: 'center', padding: '60px' }}>
            <CheckCircle size={48} style={{ color: 'var(--border)', marginBottom: '16px' }} />
            <p>No completed sessions found.</p>
            <small>Once a session is marked as complete, it will appear here.</small>
          </div>
        ) : (
          <div className="unidash-schedule-grid">
            {completedSessions.map((req) => (
              <div key={req.id} className="uni-schedule-card" style={{ opacity: 0.9 }}>
                <div className="schedule-card-top">
                  <h3 className="schedule-card-title" style={{ color: 'var(--proto-white)' }}>{req.title}</h3>
                  <span className="mode-badge" style={{ background: 'var(--cyan-dim)', color: 'var(--cyan)', border: '1px solid var(--cyan-dim)' }}>
                    COMPLETED
                  </span>
                </div>

                <div className="schedule-card-subtext" style={{ color: 'var(--text-muted)' }}>
                  <strong>{req.engagement_type}</strong> • {req.required_domain}
                </div>

                <div className="expert-confirmed-pill" style={{ background: 'var(--graphite-dark)', borderColor: 'var(--border)' }}>
                  <div className="expert-avatar-circle" style={{ overflow: 'hidden' }}>
                    {req.professor_photo ? (
                      <img
                        src={req.professor_photo.startsWith('http') ? req.professor_photo : `${BASE_URL}${req.professor_photo}`}
                        alt={req.professor_name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      req.professor_name?.charAt(0) || 'P'
                    )}
                  </div>
                  <div className="expert-info-text">
                    <span className="name" style={{ color: 'var(--proto-white)' }}>{req.professor_name || 'Expert'}</span>
                    <span className="label" style={{ color: 'var(--text-muted)' }}>Session Concluded</span>
                  </div>
                </div>

                <div className="schedule-card-footer" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                  <div className="schedule-meta-item">
                    <Calendar size={14} style={{ color: 'var(--text-faint)' }} />
                    <span style={{ color: 'var(--text-muted)' }}>
                      {formatDate(req.event_date)}
                    </span>
                  </div>
                </div>

                <div style={{ marginTop: '15px', borderTop: '1px solid var(--border)', paddingTop: '15px', display: 'flex', gap: '10px', flexDirection: 'column' }}>
                  <button
                    className="uni-btn-primary"
                    style={{ width: '100%', justifyContent: 'center', background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)', border: 'none' }}
                    onClick={() => handleGiveFeedback(req.id)}
                  >
                    <MessageSquare size={16} /> Give Feedback
                  </button>

                  {req.enrolled_students && (
                    <div style={{ marginTop: '10px' }}>
                      <button
                        className="uni-btn-secondary"
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          padding: '8px',
                          background: 'var(--graphite-dark)',
                          border: '1px solid var(--border)',
                          color: 'var(--text-muted)',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                        onClick={() => openStudentsModal(req.enrolled_students, req.title)}
                      >
                        <Users size={14} />
                        View Participants ({req.enrolled_students.length})
                      </button>
                    </div>
                  )}

                  {req.payment_id && (
                    <div>
                      <button
                        className="uni-btn-secondary"
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          padding: '8px',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          color: '#ef4444',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#fecaca'; }}
                        onClick={() => handleRequestRefund(req)}
                      >
                        <ArrowLeft size={16} />
                        Request Refund
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // --- TAB: MY EXPERTS ---
  const renderMyExperts = () => {
    return (
      <div className="unidash-fade-in">
        <div className="chart-header" style={{ marginBottom: '20px' }}>
          <Users size={18} /> <span>Engaged Industry Experts</span>
        </div>

        {loadingExperts ? (
          <div className="loading-spinner">Loading Experts...</div>
        ) : myExperts.length === 0 ? (
          <div className="uni-card empty-state" style={{ textAlign: 'center', padding: '60px' }}>
            <Users size={48} style={{ color: 'var(--border)', marginBottom: '16px' }} />
            <p>No experts found.</p>
            <small>Professors you engage with will appear here.</small>
          </div>
        ) : (
          <div className="professors-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px' }}>
            {myExperts.map((pop) => (
              <div key={pop.id} className="uni-expert-card-premium" style={{
                background: 'var(--graphite-dark)',
                borderRadius: '24px',
                padding: '24px',
                border: '1px solid var(--border)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}
              >
                {/* Decorative background element */}
                <div style={{
                  position: 'absolute', top: '-10px', right: '-10px', width: '80px', height: '80px',
                  background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.03) 0%, rgba(59, 130, 246, 0.03) 100%)',
                  borderRadius: '50%', zIndex: 0
                }}></div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative', zIndex: 1 }}>
                  <div style={{
                    width: '64px', height: '64px', borderRadius: '20px',
                    background: 'var(--cyan-dim)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '24px', fontWeight: '800', color: 'var(--cyan)',
                    border: '1px solid var(--border)', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.03)',
                    overflow: 'hidden'
                  }}>
                    {pop.profile_photo ? (
                      <img
                        src={pop.profile_photo.startsWith('http') ? pop.profile_photo : `${BASE_URL}${pop.profile_photo}`}
                        alt={pop.full_name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      pop.full_name?.charAt(0)
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '800', color: 'var(--proto-white)', margin: '0 0 4px 0', letterSpacing: '-0.01em' }}>{pop.full_name}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{
                        background: 'var(--cyan-dim)', color: 'var(--cyan)', fontSize: '11px', fontWeight: '700',
                        padding: '2px 8px', borderRadius: '10px', textTransform: 'uppercase', letterSpacing: '0.02em',
                        border: '1px solid #dcfce7'
                      }}>{pop.years_of_experience}+ Years Exp</span>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)', fontSize: '13px', fontWeight: '500' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'var(--engine-black)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--cyan)' }}>
                      <Briefcase size={14} />
                    </div>
                    <span style={{ color: 'var(--proto-white)', fontWeight: '600' }}>{pop.job_title}</span>
                    <span style={{ color: 'var(--text-faint)' }}>@</span>
                    <span style={{ color: 'var(--proto-white)', fontWeight: '600' }}>{pop.company}</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)', fontSize: '13px', fontWeight: '500' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'var(--engine-black)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
                      <Users size={14} />
                    </div>
                    <span style={{ color: 'var(--text-muted)' }}>{pop.engagement_count || 0} Successful Engagements</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '4px', position: 'relative', zIndex: 1 }}>
                  {pop.linkedin_url && (
                    <a
                      href={/^https?:\/\//i.test(pop.linkedin_url) ? pop.linkedin_url : `https://${pop.linkedin_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        padding: '10px', background: 'var(--engine-black)', border: '1px solid var(--border)', borderRadius: '12px',
                        color: 'var(--text-muted)', fontSize: '13px', fontWeight: '600', textDecoration: 'none', transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--border)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#0077b5"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
                      LinkedIn
                    </a>
                  )}
                  <button
                    onClick={() => handleOpenProposalModal(pop)}
                    style={{
                      flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      padding: '10px', background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)',
                      border: 'none', borderRadius: '12px', color: 'white', fontSize: '13px', fontWeight: '700',
                      cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 15px rgba(79, 70, 229, 0.3)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 70, 229, 0.2)'; }}
                  >
                    <Zap size={16} />
                    Book Again
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };


  // --- TAB: SETTINGS ---
  const renderMySettings = () => {
    if (isCustomRoleUser) {
      return (
        <div className="unidash-fade-in">
          <div className="chart-header" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <User size={18} />
              <span>My Profile</span>
            </div>
          </div>
          <div className="stat-card" style={{ padding: '24px', maxWidth: '600px', margin: '0 auto', textAlign: 'left' }}>
            <h3 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>Personal Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px', marginBottom: '15px', fontSize: '15px' }}>
              <strong style={{ color: 'var(--text-muted)' }}>Name:</strong>
              <span style={{ color: 'var(--text-primary)' }}>{storedUser?.name || storedUser?.full_name || 'N/A'}</span>

              <strong style={{ color: 'var(--text-muted)' }}>Email:</strong>
              <span style={{ color: 'var(--text-primary)' }}>{storedUser?.email || 'N/A'}</span>

              <strong style={{ color: 'var(--text-muted)' }}>Role:</strong>
              <span style={{ color: 'var(--text-primary)' }}>{storedUser?.role || 'Custom Role'}</span>

              <strong style={{ color: 'var(--text-muted)' }}>Department:</strong>
              <span style={{ color: 'var(--text-primary)' }}>{storedUser?.department || 'N/A'}</span>

              <strong style={{ color: 'var(--text-muted)' }}>Employee ID:</strong>
              <span style={{ color: 'var(--text-primary)' }}>{storedUser?.employee_id || 'N/A'}</span>
            </div>
          </div>
        </div>
      );
    }

    if (!uniProfile) return <div className="loading-spinner">Loading Profile...</div>;

    return (
      <div className="unidash-fade-in">
        <div className="chart-header" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Settings size={18} />
            <span>University Profile Settings</span>
          </div>
          {!isEditingSettings && (
            <button
              className="uni-btn-primary"
              onClick={() => {
                setIsEditingSettings(true);
                setEditedSettings({ ...uniProfile });
              }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px' }}
            >
              <Edit2 size={16} />
              Edit Profile
            </button>
          )}
        </div>



        {/* Settings Tabs - Premium Design */}
        <div className="settings-tabs-bar" style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          borderBottom: '1px solid var(--border)',
          paddingBottom: '0px'
        }}>
          <button
            onClick={() => setSettingsSubTab('profile')}
            style={{
              padding: '12px 20px',
              background: 'none',
              border: 'none',
              borderBottom: settingsSubTab === 'profile' ? '2px solid #3b82f6' : '2px solid transparent',
              color: settingsSubTab === 'profile' ? '#3b82f6' : 'var(--text-muted)',
              fontWeight: 600,
              fontSize: '15px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            <Building size={18} />
            Institutional Profile
          </button>

          <button
            onClick={() => setSettingsSubTab('roles')}
            style={{
              padding: '12px 20px',
              background: 'none',
              border: 'none',
              borderBottom: settingsSubTab === 'roles' ? '2px solid #3b82f6' : '2px solid transparent',
              color: settingsSubTab === 'roles' ? '#3b82f6' : 'var(--text-muted)',
              fontWeight: 600,
              fontSize: '15px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            <Shield size={18} />
            Roles & System Permissions
          </button>
          <button
            onClick={() => setSettingsSubTab('ui-config')}
            style={{
              padding: '12px 20px',
              background: 'none',
              border: 'none',
              borderBottom: settingsSubTab === 'ui-config' ? '2px solid #3b82f6' : '2px solid transparent',
              color: settingsSubTab === 'ui-config' ? '#3b82f6' : 'var(--text-muted)',
              fontWeight: 600,
              fontSize: '15px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            <Sliders size={18} />
            Page & UI Role Configuration
          </button>


        </div>



        {
          settingsSubTab === 'roles' && (
            <div className="uni-card pop-fade-up" style={{ maxWidth: '1200px', minHeight: '600px' }}>
              <RoleManagement onRolesUpdated={fetchCustomRoles} />
            </div>
          )
        }

        {
          settingsSubTab === 'ui-config' && (
            <div className="uni-card pop-fade-up" style={{ maxWidth: '1400px', minHeight: '600px' }}>
              <RoleConfigurationPage onConfigurationSaved={clearUIRulesCache} />
            </div>
          )
        }



        {
          settingsSubTab === 'profile' && (
            <div className="uni-card" style={{ maxWidth: '1200px' }}>
              {/* Header Section */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', borderBottom: '2px solid var(--border)', paddingBottom: '24px', flexWrap: 'wrap', gap: '20px' }}>
                <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Logo Container */}
                  <div style={{ position: 'relative' }}>
                    <div
                      onClick={() => !uploadingLogo && document.getElementById('uni-logo-input')?.click()}
                      title="Click to upload or change institutional logo"
                      style={{
                        width: '104px',
                        height: '104px',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        position: 'relative',
                        cursor: uploadingLogo ? 'wait' : 'pointer',
                        border: '2px solid var(--border)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: uniProfile.logo_url ? 'var(--graphite, #1e293b)' : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.25)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                    >
                      {uniProfile.logo_url ? (
                        <img
                          src={getLogoUrl(uniProfile.logo_url)}
                          alt={uniProfile.institution_name || 'Institution Logo'}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: '38px', fontWeight: '800', color: '#ffffff' }}>
                          {uniProfile.institution_name?.charAt(0) || <Building2 size={36} />}
                        </span>
                      )}

                      {/* Hover Overlay with Camera Icon */}
                      {!uploadingLogo && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(0, 0, 0, 0.45)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                            color: '#ffffff',
                            fontSize: '11px',
                            fontWeight: 600,
                            borderRadius: '14px'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0'; }}
                        >
                          <Camera size={20} />
                          <span>{uniProfile.logo_url ? 'Change' : 'Upload'}</span>
                        </div>
                      )}

                      {/* Loading Spinner during upload */}
                      {uploadingLogo && (
                        <div style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'rgba(11, 15, 20, 0.75)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '14px'
                        }}>
                          <RefreshCw className="spin" size={24} color="#3b82f6" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '6px' }}>
                      {uniProfile.institution_name}
                    </h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '15px', marginBottom: '8px' }}>{uniProfile.institution_type}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <div className="uni-profile-badge" style={{
                        background: 'var(--cyan-dim)',
                        color: 'var(--cyan)',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <CheckCircle size={14} />
                        Verified Institution
                      </div>

                      {/* Hidden File Input */}
                      <input
                        type="file"
                        id="uni-logo-input"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleLogoUpload(e.target.files[0]);
                            e.target.value = '';
                          }
                        }}
                      />

                      {/* Upload / Change Button */}
                      <button
                        type="button"
                        onClick={() => document.getElementById('uni-logo-input')?.click()}
                        disabled={uploadingLogo}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 14px',
                          borderRadius: '8px',
                          border: '1px solid var(--border)',
                          background: 'var(--graphite-dark, #1e293b)',
                          color: 'var(--proto-white, #f8fafc)',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        title="Upload institutional logo"
                      >
                        <Upload size={13} />
                        {uploadingLogo ? 'Uploading...' : uniProfile.logo_url ? 'Change Logo' : 'Upload Logo'}
                      </button>

                      {/* Remove Button */}
                      {uniProfile.logo_url && (
                        <button
                          type="button"
                          onClick={handleLogoDelete}
                          disabled={uploadingLogo}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            background: 'rgba(239, 68, 68, 0.08)',
                            color: '#ef4444',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          title="Remove institutional logo"
                        >
                          <Trash2 size={13} />
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Settings Sections */}
              <div style={{ display: 'grid', gap: '30px' }}>

                {/* BASIC INFORMATION */}
                <div className="settings-section">
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Building size={20} style={{ color: '#3b82f6' }} />
                    Basic Information
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Institution Name *
                      </label>
                      {isEditingSettings ? (
                        <input
                          type="text"
                          value={editedSettings.institution_name || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, institution_name: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>{uniProfile.institution_name}</div>
                      )}
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Institution Type *
                      </label>
                      {isEditingSettings ? (
                        <select
                          value={editedSettings.institution_type || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, institution_type: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        >
                          <option value="University">University</option>
                          <option value="College">College</option>
                          <option value="Institute">Institute</option>
                          <option value="Academy">Academy</option>
                          <option value="School">School</option>
                        </select>
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>{uniProfile.institution_type}</div>
                      )}
                    </div>

                    <div className="uni-setting-group" style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Description / About
                      </label>
                      {isEditingSettings ? (
                        <textarea
                          value={editedSettings.description || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, description: e.target.value })}
                          placeholder="Brief description of your institution..."
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500',
                            minHeight: '100px',
                            fontFamily: 'inherit',
                            resize: 'vertical'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.description || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>No description provided</span>}
                        </div>
                      )}
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Website URL
                      </label>
                      {isEditingSettings ? (
                        <input
                          type="url"
                          value={editedSettings.website_url || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, website_url: e.target.value })}
                          placeholder="https://www.example.edu"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.website_url ? (
                            <a href={uniProfile.website_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>
                              {uniProfile.website_url}
                            </a>
                          ) : (
                            <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not provided</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Establishment Year
                      </label>
                      {isEditingSettings ? (
                        <input
                          type="number"
                          value={editedSettings.establishment_year || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, establishment_year: parseInt(e.target.value) })}
                          placeholder="e.g., 1950"
                          min="1800"
                          max={new Date().getFullYear()}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.establishment_year || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not provided</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* CONTACT DETAILS */}
                <div className="settings-section" style={{ borderTop: '1px solid var(--border)', paddingTop: '30px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Mail size={20} style={{ color: '#10b981' }} />
                    Contact Details
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Contact Person *
                      </label>
                      {isEditingSettings ? (
                        <input
                          type="text"
                          value={editedSettings.contact_person || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, contact_person: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>{uniProfile.contact_person}</div>
                      )}
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Contact Email
                      </label>
                      {isEditingSettings ? (
                        <input
                          type="email"
                          value={editedSettings.contact_email || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, contact_email: e.target.value })}
                          placeholder="contact@university.edu"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.contact_email || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not provided</span>}
                        </div>
                      )}
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Contact Phone
                      </label>
                      {isEditingSettings ? (
                        <div className="phone-input-container">
                          <PhoneInput
                            country={'in'}
                            value={editedSettings.contact_phone || ''}
                            onChange={value => setEditedSettings({ ...editedSettings, contact_phone: value })}
                            enableSearch={true}
                            disableSearchIcon={true}
                            placeholder="e.g. +91 9876543210"
                            inputProps={{
                              name: 'contact_phone',
                              required: true,
                            }}
                            containerClass="react-tel-input"
                            inputClass="uni-input"
                            style={{ width: '100%' }}
                            buttonStyle={{ borderRadius: '8px 0 0 8px', border: '1px solid var(--border)', borderRight: 'none', background: 'var(--graphite-dark)' }}
                            inputStyle={{ width: '100%', padding: '10px 10px 10px 48px', borderRadius: '8px', border: '2px solid var(--border)', height: '42px' }}
                          />
                        </div>
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.contact_phone || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not provided</span>}
                        </div>
                      )}
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Account Email
                      </label>
                      <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {uniProfile.email || 'N/A'}
                        <span style={{ fontSize: '11px', background: 'var(--border)', color: 'var(--text-muted)', padding: '2px 8px', borderRadius: '4px' }}>Cannot be changed</span>
                      </div>
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Account Phone
                      </label>
                      {isEditingSettings ? (
                        <div className="phone-input-container">
                          <PhoneInput
                            country={'in'}
                            value={editedSettings.phone_number || ''}
                            onChange={value => setEditedSettings({ ...editedSettings, phone_number: value })}
                            enableSearch={true}
                            disableSearchIcon={true}
                            placeholder="Account Phone Number"
                            inputProps={{
                              name: 'phone_number',
                              required: true,
                            }}
                            containerClass="react-tel-input"
                            inputClass="uni-input"
                            style={{ width: '100%' }}
                            buttonStyle={{ borderRadius: '8px 0 0 8px', border: '1px solid var(--border)', borderRight: 'none', background: 'var(--graphite-dark)' }}
                            inputStyle={{ width: '100%', padding: '10px 10px 10px 48px', borderRadius: '8px', border: '1px solid var(--border)', height: '42px' }}
                          />
                        </div>
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {uniProfile.phone_number || 'N/A'}
                          <span style={{ fontSize: '11px', background: 'var(--border)', color: 'var(--text-muted)', padding: '2px 8px', borderRadius: '4px' }}>Verified</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* LOCATION & ADDRESS */}
                <div className="settings-section" style={{ borderTop: '1px solid var(--border)', paddingTop: '30px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <MapPin size={20} style={{ color: '#f59e0b' }} />
                    Location & Address
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div className="uni-setting-group" style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Address Line 1
                      </label>
                      {isEditingSettings ? (
                        <input
                          type="text"
                          value={editedSettings.address_line1 || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, address_line1: e.target.value })}
                          placeholder="Street address, building name, etc."
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.address_line1 || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not provided</span>}
                        </div>
                      )}
                    </div>

                    <div className="uni-setting-group" style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Address Line 2
                      </label>
                      {isEditingSettings ? (
                        <input
                          type="text"
                          value={editedSettings.address_line2 || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, address_line2: e.target.value })}
                          placeholder="Landmark, area, etc. (optional)"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.address_line2 || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not provided</span>}
                        </div>
                      )}
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        City *
                      </label>
                      {isEditingSettings ? (
                        <input
                          type="text"
                          value={editedSettings.city || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, city: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>{uniProfile.city}</div>
                      )}
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        State / Province
                      </label>
                      {isEditingSettings ? (
                        <input
                          type="text"
                          value={editedSettings.state || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, state: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.state || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not provided</span>}
                        </div>
                      )}
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Country
                      </label>
                      {isEditingSettings ? (
                        <input
                          type="text"
                          value={editedSettings.country || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, country: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.country || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not provided</span>}
                        </div>
                      )}
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Postal / ZIP Code
                      </label>
                      {isEditingSettings ? (
                        <input
                          type="text"
                          value={editedSettings.postal_code || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, postal_code: e.target.value })}
                          maxLength="6"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.postal_code || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not provided</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* INSTITUTIONAL DETAILS */}
                <div className="settings-section" style={{ borderTop: '1px solid var(--border)', paddingTop: '30px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Award size={20} style={{ color: '#8b5cf6' }} />
                    Institutional Details
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Accreditation
                      </label>
                      {isEditingSettings ? (
                        <input
                          type="text"
                          value={editedSettings.accreditation || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, accreditation: e.target.value })}
                          placeholder="e.g., NAAC A++, NBA"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.accreditation || <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not provided</span>}
                        </div>
                      )}
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Student Strength
                      </label>
                      {isEditingSettings ? (
                        <input
                          type="number"
                          value={editedSettings.student_strength || ''}
                          onChange={(e) => setEditedSettings({ ...editedSettings, student_strength: parseInt(e.target.value) })}
                          placeholder="Total number of students"
                          min="0"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        />
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.student_strength ? uniProfile.student_strength.toLocaleString() : <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Not provided</span>}
                        </div>
                      )}
                    </div>

                    <div className="uni-setting-group" style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Preferred Domains
                      </label>
                      {isEditingSettings ? (
                        <select
                          multiple
                          value={editedSettings.preferred_domains || []}
                          onChange={(e) => {
                            const selected = Array.from(e.target.selectedOptions, option => option.value);
                            setEditedSettings({ ...editedSettings, preferred_domains: selected });
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500',
                            minHeight: '120px'
                          }}
                        >
                          {INDUSTRY_DOMAINS.map(domain => (
                            <option key={domain} value={domain}>{domain}</option>
                          ))}
                        </select>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {uniProfile.preferred_domains && uniProfile.preferred_domains.length > 0 ? (
                            uniProfile.preferred_domains.map(domain => (
                              <span key={domain} style={{
                                background: 'var(--vector-blue-dim)',
                                color: 'var(--vector-blue)',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: '500'
                              }}>
                                {domain}
                              </span>
                            ))
                          ) : (
                            <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>No domains selected</span>
                          )}
                        </div>
                      )}
                      {isEditingSettings && (
                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>Hold Ctrl/Cmd to select multiple domains</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* PREFERENCES */}
                <div className="settings-section" style={{ borderTop: '1px solid var(--border)', paddingTop: '30px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Sliders size={20} style={{ color: '#ec4899' }} />
                    Preferences & Settings
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Session Approval Mode
                      </label>
                      {isEditingSettings ? (
                        <select
                          value={editedSettings.session_approval_mode || 'MANUAL'}
                          onChange={(e) => setEditedSettings({ ...editedSettings, session_approval_mode: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        >
                          <option value="MANUAL">Manual Approval</option>
                          <option value="AUTO">Auto Approval</option>
                        </select>
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.session_approval_mode === 'AUTO' ? 'Auto Approval' : 'Manual Approval'}
                        </div>
                      )}
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Profile Visibility
                      </label>
                      {isEditingSettings ? (
                        <select
                          value={editedSettings.profile_visibility || 'PUBLIC'}
                          onChange={(e) => setEditedSettings({ ...editedSettings, profile_visibility: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid var(--border)',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}
                        >
                          <option value="PUBLIC">Public</option>
                          <option value="PRIVATE">Private</option>
                        </select>
                      ) : (
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          {uniProfile.profile_visibility || 'Public'}
                        </div>
                      )}
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: isEditingSettings ? 'pointer' : 'default' }}>
                        {isEditingSettings ? (
                          <input
                            type="checkbox"
                            checked={editedSettings.notification_email_enabled !== false}
                            onChange={(e) => setEditedSettings({ ...editedSettings, notification_email_enabled: e.target.checked })}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                        ) : (
                          <div style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '4px',
                            background: uniProfile.notification_email_enabled !== false ? '#10b981' : '#e5e7eb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                          }}>
                            {uniProfile.notification_email_enabled !== false && <CheckCircle size={14} />}
                          </div>
                        )}
                        <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--proto-white)' }}>Email Notifications</span>
                      </label>
                    </div>

                    <div className="uni-setting-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: isEditingSettings ? 'pointer' : 'default' }}>
                        {isEditingSettings ? (
                          <input
                            type="checkbox"
                            checked={editedSettings.notification_sms_enabled === true}
                            onChange={(e) => setEditedSettings({ ...editedSettings, notification_sms_enabled: e.target.checked })}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                        ) : (
                          <div style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '4px',
                            background: uniProfile.notification_sms_enabled === true ? '#10b981' : '#e5e7eb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                          }}>
                            {uniProfile.notification_sms_enabled === true && <CheckCircle size={14} />}
                          </div>
                        )}
                        <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--proto-white)' }}>SMS Notifications</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* SECURITY - CHANGE PASSWORD */}
                <div className="settings-section" style={{ borderTop: '1px solid var(--border)', paddingTop: '30px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Lock size={20} style={{ color: '#ef4444' }} />
                    Security
                  </h3>
                  <div style={{ display: 'grid', gap: '20px' }}>
                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Password
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                          ••••••••••••
                        </div>
                        <button
                          onClick={() => setShowChangePasswordModal(true)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: '1px solid var(--border)',
                            background: 'var(--graphite-dark)',
                            color: '#3b82f6',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#eff6ff';
                            e.target.style.borderColor = '#3b82f6';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'white';
                            e.target.style.borderColor = 'var(--border)';
                          }}
                        >
                          <Lock size={14} />
                          Change Password
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ACCOUNT INFO */}
                <div className="settings-section" style={{ borderTop: '1px solid var(--border)', paddingTop: '30px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Calendar size={20} style={{ color: 'var(--text-muted)' }} />
                    Account Information
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div className="uni-setting-group">
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                        Member Since
                      </label>
                      <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--proto-white)' }}>
                        {uniProfile.created_at ? new Date(uniProfile.created_at).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : 'Jan 2026'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {isEditingSettings && (
                <div style={{
                  marginTop: '30px',
                  paddingTop: '20px',
                  borderTop: '2px solid var(--border)',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={() => {
                      setIsEditingSettings(false);
                      setEditedSettings({});
                    }}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: '2px solid var(--border)',
                      background: 'var(--graphite-dark)',
                      color: 'var(--text-muted)',
                      fontWeight: '600',
                      fontSize: '15px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = 'var(--border)';
                      e.target.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = 'var(--border)';
                      e.target.style.background = 'white';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveSettings}
                    className="uni-btn-primary"
                    style={{
                      padding: '12px 32px',
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Save size={18} />
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          )
        }
      </div >
    );
  };


  // --- TAB: STUDENTS ---
  const handlePreviewUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const res = await api.post('/students/upload-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadPreview(res.data);
      setUploadStep('preview');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to process file', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleCommitUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const res = await api.post('/students/upload-commit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      let finalResult = res.data;

      // Removed LMS sync logic for students per request

      setUploadResult(finalResult);
      setUploadStep('result');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create accounts', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteStudent = async (student) => {
    setConfirmModal({
      isOpen: true,
      title: 'Confirm Delete',
      message: `Are you sure you want to permanently delete ${student.full_name || 'this student'}? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await api.delete(`/students/${student.id}`);
          fetchStudents(activeTab === 'deactivated_students' ? 'deactivated' : 'active');
          showToast('Student deleted successfully.', 'success');
        } catch (err) {
          console.error("Error deleting student:", err);
          showToast(err.response?.data?.detail || "Failed to delete student", "error");
        }
      }
    });
  };

  const handleOpenEditStudent = (student) => {
    setEditStudentModal({ show: true, student });
    setEditStudentForm({
      full_name: student.full_name || '',
      phone_number: student.phone_number || '',
      student_date_of_birth: student.student_date_of_birth || '',
      gender: student.gender || '',
      usn: student.usn || '',
      graduation_level: student.graduation_level || '',
      program: student.program || '',
      academic_year: student.academic_year || '',
      academic_batch: student.academic_batch || '',
      admission_semester: student.admission_semester || '',
      branch: student.branch || ''
    });
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    if (editStudentForm.phone_number && !/^\d{10}$/.test(editStudentForm.phone_number.trim())) {
      showToast("Phone number must contain exactly 10 digits.", "error");
      return;
    }
    try {
      await api.put(`/students/${editStudentModal.student.id}`, editStudentForm);
      showToast("Student updated successfully.", "success");
      setEditStudentModal({ show: false, student: null });
      fetchStudents();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to update student.", "error");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/students/template', { responseType: 'blob' });
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Onboarding_Template_Students.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      showToast("Failed to download template.", 'error');
      console.error(err);
    }
  };

  const renderStudentsSection = (isDeactivatedView = false) => {
    const filteredStudents = students.filter(student => {
      const q = studentSearchQuery.toLowerCase();
      if (!q) return true;
      return (
        (student.full_name && String(student.full_name).toLowerCase().includes(q)) ||
        (student.email && String(student.email).toLowerCase().includes(q)) ||
        (student.usn && String(student.usn).toLowerCase().includes(q)) ||
        (student.student_id && String(student.student_id).toLowerCase().includes(q))
      );
    });

    // Group students by Branch and Academic Batch or Graduation Year
    const groupedStudents = filteredStudents.reduce((groups, student) => {
      const year = student.academic_batch || student.graduation_year;
      const branch = student.branch || student.degree || student.department;
      const gradLevel = student.graduation_level;
      const program = student.program;

      let cohortKey = 'Unknown Batch';
      const parts = [];
      if (gradLevel) parts.push(String(gradLevel).trim());
      if (program) parts.push(String(program).trim());
      if (branch) parts.push(String(branch).trim());

      const baseString = parts.join(' - ');
      if (baseString && year) {
        cohortKey = `${baseString} (${String(year).trim()})`;
      } else if (baseString) {
        cohortKey = baseString;
      } else if (year) {
        cohortKey = String(year).trim();
      }

      if (!groups[cohortKey]) groups[cohortKey] = [];
      groups[cohortKey].push(student);
      return groups;
    }, {});

    const sortedYears = Object.keys(groupedStudents).sort((a, b) => {
      if (a === 'Unknown Batch') return 1;
      if (b === 'Unknown Batch') return -1;
      return a.localeCompare(b);
    });

    const headerSection = (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-main, #1e293b)', margin: '0 0 4px 0' }}>
            {isDeactivatedView ? 'Deactivated Students' : 'Student Management'}
          </h2>
          <p style={{ color: 'var(--text-muted, #64748b)', margin: 0, fontSize: '0.95rem' }}>
            {isDeactivatedView ? 'View and reactivate deactivated student accounts.' : 'Manage student candidates and their enrollment records.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {!isDeactivatedView && (
            <UIElementGuard page="student_directory" element="add_students_btn">
              <button
                onClick={() => {
                  setShowUploadModal(true);
                  setUploadStep('select');
                  setUploadFile(null);
                  setUploadPreview(null);
                  setUploadResult(null);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  borderRadius: '8px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
                }}
              >
                <Upload size={16} /> Add Students
              </button>
            </UIElementGuard>
          )}
        </div>
      </div>
    );

    if (loadingStudents) {
      return <div className="loading-spinner">Loading Students...</div>;
    }

    if (students.length === 0) {
      return (
        <div className="unidash-fade-in">
          {permissionError && (
            <div style={{ padding: '20px', background: '#fee2e2', border: '1px solid #ef4444', color: '#b91c1c', borderRadius: '10px', marginBottom: '20px', fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>🚫</span> {permissionError}
            </div>
          )}

          {headerSection}

          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ color: '#64748b', fontSize: '0.9rem', fontWeight: '500' }}>
              0 Students Registered
            </div>
          </div>

          <div className="uni-card empty-state" style={{ textAlign: 'center', padding: '60px' }}>
            <Users size={48} style={{ color: 'var(--border)', marginBottom: '16px' }} />
            <p>No students found.</p>
            <small>Upload an Excel file to bulk create student accounts.</small>
          </div>
        </div>
      );
    }

    const isDetailView = selectedCohort || studentSearchQuery;
    const cohortStudents = selectedCohort ? (groupedStudents[selectedCohort] || []) : filteredStudents;

    // Pagination calculations (10 students per page)
    const STUDENTS_PER_PAGE = 10;
    const totalCohortStudents = cohortStudents.length;
    const totalPages = Math.ceil(totalCohortStudents / STUDENTS_PER_PAGE) || 1;
    const currentPage = Math.min(Math.max(studentCurrentPage, 1), totalPages);
    const startIndex = (currentPage - 1) * STUDENTS_PER_PAGE;
    const endIndex = Math.min(startIndex + STUDENTS_PER_PAGE, totalCohortStudents);
    const paginatedStudents = cohortStudents.slice(startIndex, endIndex);

    return (
      <div className="unidash-fade-in">
        {headerSection}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px', flexWrap: 'wrap' }}>
          {isDetailView ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={() => { setSelectedCohort(null); setStudentSearchQuery(''); setStudentCurrentPage(1); }}
                style={{
                  border: '1px solid var(--border)', background: 'var(--graphite-dark)', cursor: 'pointer', padding: '8px',
                  display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '10px',
                  transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--text-faint)'; e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'white'; }}
              >
                <ArrowLeft size={20} />
              </button>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <h2 style={{ fontSize: '22px', color: 'var(--proto-white)', margin: 0, fontWeight: '800' }}>
                    {selectedCohort || 'Search Results'}
                  </h2>
                  <span className="seniority-tag" style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>
                    {cohortStudents.length} {cohortStudents.length === 1 ? 'Student' : 'Students'}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ position: 'relative', flex: '1', minWidth: '250px', maxWidth: '400px' }}>
              <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                type="text"
                placeholder="Search students..."
                value={studentSearchQuery}
                onChange={(e) => { setStudentSearchQuery(e.target.value); setStudentCurrentPage(1); }}
                style={{ width: '100%', padding: '10px 12px 10px 38px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          )}

          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            {isDetailView && (
              <div style={{ position: 'relative', width: '250px' }}>
                <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder="Search students..."
                  value={studentSearchQuery}
                  onChange={(e) => { setStudentSearchQuery(e.target.value); setStudentCurrentPage(1); }}
                  style={{ width: '100%', padding: '10px 12px 10px 38px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            )}
            {!isDetailView && (
              <div style={{ color: '#64748b', fontSize: '0.9rem', fontWeight: '500' }}>
                {filteredStudents.length} Students across {sortedYears.length} Batches
              </div>
            )}
          </div>
        </div>

        {selectedStudentProfile ? (
          <StudentProfileDetail
            student={selectedStudentProfile}
            onBack={() => setSelectedStudentProfile(null)}
            coursesData={studentCourses}
            loadingCourses={loadingStudentCourses}
          />
        ) : isDetailView ? (
          <div className="uni-card" style={{ padding: '0px', overflow: 'hidden', border: '1px solid var(--border)' }}>
            <table className="uni-roster-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: 'var(--engine-black)' }}>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '16px 24px', textAlign: 'left', color: 'var(--text-muted)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Student Profile</th>
                  <th style={{ padding: '16px 24px', textAlign: 'left', color: 'var(--text-muted)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Academic Info</th>
                  <th style={{ padding: '16px 24px', textAlign: 'left', color: 'var(--text-muted)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Additional Details</th>
                  <th style={{ padding: '16px 24px', textAlign: 'left', color: 'var(--text-muted)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                  <th style={{ padding: '16px 24px', textAlign: 'left', color: 'var(--text-muted)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Onboarding</th>
                  <th style={{ padding: '16px 24px', textAlign: 'right', color: 'var(--text-muted)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}></th>
                </tr>
              </thead>
              <tbody>
                {paginatedStudents.map((student) => (
                  <tr key={student.id} style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#fcfdfe'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <td style={{ padding: '16px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px', height: '40px',
                          background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                          color: '#4338ca',
                          borderRadius: '12px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: '700', fontSize: '14px', border: '1px solid #e0e7ff'
                        }}>
                          {student.full_name?.charAt(0) || 'S'}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', color: 'var(--proto-white)', fontSize: '15px' }}>{student.full_name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{student.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)' }}>{student.degree || 'Not Set'}</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>ID: {student.student_id || 'N/A'}</span>
                      </div>
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontSize: '13px', color: 'var(--proto-white)', fontWeight: '500' }}>
                          Grad Level: <span style={{ color: 'var(--text-muted)' }}>{student.graduation_level || 'N/A'}</span>
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          Program: <span style={{ color: '#10b981', fontWeight: '600' }}>{student.program || 'N/A'}</span>
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>
                          DOB: {student.student_date_of_birth || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                        background: student.is_active ? '#ecfdf5' : '#fff1f2',
                        color: student.is_active ? '#059669' : '#e11d48',
                        border: `1px solid ${student.is_active ? '#d1fae5' : '#ffe4e6'}`
                      }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }}></div>
                        {student.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </div>
                    </td>
                    <td style={{ padding: '16px 24px', color: 'var(--text-muted)', fontSize: '13px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Clock size={14} />
                        {student.created_at ? new Date(student.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '-'}
                      </div>
                    </td>
                    <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                        <button
                          onClick={() => handleToggleStudentStatus(student)}
                          style={{
                            background: student.is_active ? '#fff1f2' : '#f0fdf4',
                            color: student.is_active ? '#e11d48' : '#16a34a',
                            border: `1px solid ${student.is_active ? '#fda4af' : '#86efac'}`,
                            cursor: 'pointer',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            fontWeight: '700',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title={student.is_active ? "Deactivate Student Account" : "Activate Student Account"}
                        >
                          {student.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <UIElementGuard page="student_directory" element="delete_student_btn">
                        <button
                          onClick={() => handleDeleteStudent(student)}
                          style={{
                            background: '#fff1f2',
                            color: '#e11d48',
                            border: '1px solid #fda4af',
                            cursor: 'pointer',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            fontWeight: '700',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#ffe4e6'; e.currentTarget.style.borderColor = '#fb7185'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff1f2'; e.currentTarget.style.borderColor = '#fda4af'; }}
                          title="Delete Student Account"
                        >
                          Delete
                        </button>
                        </UIElementGuard>
                        <button
                          onClick={() => handleViewStudentProfile(student)}
                          title="View Profile"
                          style={{
                            color: 'var(--cyan)', padding: '8px', border: 'none', background: 'none',
                            cursor: 'pointer', borderRadius: '8px', transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--cyan-dim)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                        >
                          <Eye size={16} />
                        </button>
                        <UIElementGuard page="student_directory" element="edit_student_btn">
                          <button
                            onClick={() => handleOpenEditStudent(student)}
                            title="Edit Student"
                            style={{
                              color: 'var(--cyan)', padding: '8px', border: 'none', background: 'none',
                              cursor: 'pointer', borderRadius: '8px', transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--cyan-dim)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                          >
                            <Pencil size={16} />
                          </button>
                        </UIElementGuard>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Controls (10 students per page) */}
            {totalCohortStudents > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 24px',
                borderTop: '1px solid var(--border, rgba(245, 247, 250, 0.1))',
                background: 'var(--graphite-dark, #f8fafc)',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <div style={{ fontSize: '13px', color: 'var(--text-muted, #64748b)' }}>
                  Showing <strong style={{ color: 'var(--text-main, #1e293b)' }}>{totalCohortStudents === 0 ? 0 : startIndex + 1}</strong> to <strong style={{ color: 'var(--text-main, #1e293b)' }}>{endIndex}</strong> of <strong style={{ color: 'var(--text-main, #1e293b)' }}>{totalCohortStudents}</strong> students
                  {totalPages > 1 && (
                    <span style={{ marginLeft: '8px', color: 'var(--text-faint, #94a3b8)', fontSize: '12px' }}>
                      (Page {currentPage} of {totalPages})
                    </span>
                  )}
                </div>

                {totalPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button
                      onClick={() => setStudentCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border, #cbd5e1)',
                        background: currentPage === 1 ? 'transparent' : 'var(--bg-card, #ffffff)',
                        color: currentPage === 1 ? 'var(--text-faint, #94a3b8)' : 'var(--text-main, #1e293b)',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        opacity: currentPage === 1 ? 0.4 : 1
                      }}
                      title="Previous Page"
                    >
                      <ChevronLeft size={14} /> Prev
                    </button>

                    {(() => {
                      const pages = [];
                      const maxButtons = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
                      let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                      if (endPage - startPage + 1 < maxButtons) {
                        startPage = Math.max(1, endPage - maxButtons + 1);
                      }

                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => setStudentCurrentPage(1)}
                            style={{
                              minWidth: '32px',
                              height: '32px',
                              padding: '0 6px',
                              borderRadius: '8px',
                              border: '1px solid var(--border, #cbd5e1)',
                              background: 'var(--bg-card, #ffffff)',
                              color: 'var(--text-main, #1e293b)',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}
                          >
                            1
                          </button>
                        );
                        if (startPage > 2) {
                          pages.push(<span key="start-ellipsis" style={{ color: 'var(--text-faint, #94a3b8)', padding: '0 4px', fontSize: '12px' }}>…</span>);
                        }
                      }

                      for (let p = startPage; p <= endPage; p++) {
                        const isActive = p === currentPage;
                        pages.push(
                          <button
                            key={p}
                            onClick={() => setStudentCurrentPage(p)}
                            style={{
                              minWidth: '32px',
                              height: '32px',
                              padding: '0 6px',
                              borderRadius: '8px',
                              border: isActive ? '1px solid var(--cyan, #3b82f6)' : '1px solid var(--border, #cbd5e1)',
                              background: isActive ? 'var(--cyan, #3b82f6)' : 'var(--bg-card, #ffffff)',
                              color: isActive ? '#0b0f14' : 'var(--text-main, #1e293b)',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: isActive ? '700' : '600',
                              boxShadow: isActive ? '0 2px 8px rgba(25, 211, 255, 0.35)' : 'none',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {p}
                          </button>
                        );
                      }

                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(<span key="end-ellipsis" style={{ color: 'var(--text-faint, #94a3b8)', padding: '0 4px', fontSize: '12px' }}>…</span>);
                        }
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => setStudentCurrentPage(totalPages)}
                            style={{
                              minWidth: '32px',
                              height: '32px',
                              padding: '0 6px',
                              borderRadius: '8px',
                              border: '1px solid var(--border, #cbd5e1)',
                              background: 'var(--bg-card, #ffffff)',
                              color: 'var(--text-main, #1e293b)',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}
                          >
                            {totalPages}
                          </button>
                        );
                      }
                      return pages;
                    })()}

                    <button
                      onClick={() => setStudentCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border, #cbd5e1)',
                        background: currentPage === totalPages ? 'transparent' : 'var(--bg-card, #ffffff)',
                        color: currentPage === totalPages ? 'var(--text-faint, #94a3b8)' : 'var(--text-main, #1e293b)',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        opacity: currentPage === totalPages ? 0.4 : 1
                      }}
                      title="Next Page"
                    >
                      Next <ChevronRight size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (

          /* Grid of Cards */
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px' }}>
            {sortedYears.map(year => (
              <div
                key={year}
                onClick={() => { setSelectedCohort(year); setStudentCurrentPage(1); }}
                style={{
                  background: 'var(--graphite-dark)',
                  borderRadius: '24px',
                  padding: '32px 24px',
                  border: '1px solid var(--border)',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
                  e.currentTarget.style.borderColor = '#4f46e5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}
              >
                {/* Background Accent */}
                <div style={{
                  position: 'absolute', top: '-20px', right: '-20px',
                  width: '100px', height: '100px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.05) 0%, rgba(79, 70, 229, 0) 100%)',
                  zIndex: 0
                }}></div>

                <div style={{
                  width: '80px', height: '80px', borderRadius: '24px',
                  background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                  color: 'var(--cyan)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '24px', position: 'relative', zIndex: 1,
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                }}>
                  <GraduationCap size={40} strokeWidth={1.5} />
                </div>

                <h3 style={{ fontSize: '18px', fontWeight: '800', color: 'var(--proto-white)', margin: '0 0 12px 0', position: 'relative', zIndex: 1, padding: '0 10px', textAlign: 'center' }}>
                  {year}
                </h3>

                <div style={{
                  background: '#4f46e5', color: 'white',
                  padding: '6px 16px', borderRadius: '30px',
                  fontSize: '13px', fontWeight: '700',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.3)',
                  position: 'relative', zIndex: 1
                }}>
                  <Users size={14} />
                  {groupedStudents[year].length} Students
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Student Modal */}
        {editStudentModal.show && editStudentModal.student && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', padding: '32px', borderRadius: '16px', width: '90%', maxWidth: '500px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b', fontWeight: '700' }}>Edit Student</h3>
                <button onClick={() => setEditStudentModal({ show: false, student: null })} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
              </div>

              <form onSubmit={handleUpdateStudent}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Name as SSLC</label>
                    <input type="text" required value={editStudentForm.full_name} onChange={e => setEditStudentForm({ ...editStudentForm, full_name: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Student Mobile Number</label>
                    <input type="text" value={editStudentForm.phone_number} onChange={e => setEditStudentForm({ ...editStudentForm, phone_number: e.target.value.replace(/\D/g, '').slice(0, 10) })} maxLength={10} placeholder="e.g. 9876543210" style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Student Date of Birth</label>
                    <input type="text" value={editStudentForm.student_date_of_birth} onChange={e => setEditStudentForm({ ...editStudentForm, student_date_of_birth: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Student Gender</label>
                    <input type="text" value={editStudentForm.gender} onChange={e => setEditStudentForm({ ...editStudentForm, gender: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>USN</label>
                    <input type="text" value={editStudentForm.usn} onChange={e => setEditStudentForm({ ...editStudentForm, usn: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Graduation Level</label>
                    <input type="text" value={editStudentForm.graduation_level} onChange={e => setEditStudentForm({ ...editStudentForm, graduation_level: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Program</label>
                    <input type="text" value={editStudentForm.program} onChange={e => setEditStudentForm({ ...editStudentForm, program: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Academic Year</label>
                    <input type="text" value={editStudentForm.academic_year} onChange={e => setEditStudentForm({ ...editStudentForm, academic_year: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Academic Batch</label>
                    <input type="text" value={editStudentForm.academic_batch} onChange={e => setEditStudentForm({ ...editStudentForm, academic_batch: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Admission Semester</label>
                    <input type="text" value={editStudentForm.admission_semester} onChange={e => setEditStudentForm({ ...editStudentForm, admission_semester: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Branch</label>
                    <input type="text" value={editStudentForm.branch} onChange={e => setEditStudentForm({ ...editStudentForm, branch: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#1e293b', boxSizing: 'border-box' }} />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <button type="button" onClick={() => setEditStudentModal({ show: false, student: null })} style={{ padding: '10px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                  <button type="submit" style={{ padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCompaniesModule = () => {
    return (
      <div className="unidash-fade-in" style={{ padding: '20px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Corporate Partners & Companies</h2>
            <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '14px' }}>View registered corporate profiles and placement opportunities.</p>
          </div>
        </div>

        {loadingCompanies ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading companies...</div>
        ) : companies.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', background: 'white', borderRadius: '12px', border: '1px dashed #cbd5e1', color: '#64748b' }}>
            No corporate partners available. Companies are synced automatically.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {companies.map((comp, idx) => (
              <div key={idx} style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '22px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#ffedd5', color: '#ea580c', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {comp.logo_url ? <img src={comp.logo_url} alt={comp.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Building2 size={22} />}
                  </div>
                  <span style={{ background: '#eff6ff', color: '#2563eb', fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '20px' }}>{comp.status}</span>
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '0 0 6px 0' }}>{comp.name}</h3>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>Industry: <strong style={{ color: '#334155' }}>{comp.industry || 'N/A'}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #f1f5f9', paddingTop: '14px', fontSize: '13px', color: '#475569' }}>
                  <span>💼 <strong>{comp.open_roles}</strong> Open Roles</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderReportsModule = () => {
    return (
      <div className="unidash-fade-in" style={{ padding: '20px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Reports & Compliance Center</h2>
            <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '14px' }}>Generate, access, and download institutional academic, placement, and attendance reports.</p>
          </div>
          <button className="uni-btn-primary" onClick={() => showToast('New custom report generator initiated', 'success')}>
            <FileText size={18} /> <span>Generate New Report</span>
          </button>
        </div>

        <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '13px', color: '#64748b', fontWeight: '700' }}>
                <th style={{ padding: '16px 20px' }}>Report Title</th>
                <th style={{ padding: '16px 20px' }}>Category</th>
                <th style={{ padding: '16px 20px' }}>Generated Date</th>
                <th style={{ padding: '16px 20px' }}>Format</th>
                <th style={{ padding: '16px 20px', textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {[
                { title: "Semester Academic Performance & Grading Audit", category: "Academic", date: "15 Jul 2026", format: "PDF / Excel" },
                { title: "Annual Placement Readiness & Recruitment Summary", category: "Placement", date: "10 Jul 2026", format: "PDF" },
                { title: "Department Course Completion & Attendance Record", category: "Curriculum", date: "05 Jul 2026", format: "Excel" },
                { title: "Industry Professor Engagement & Guest Lecture Audit", category: "Industry", date: "30 Jun 2026", format: "PDF" }
              ].map((rep, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#334155' }}>
                  <td style={{ padding: '16px 20px', fontWeight: '600', color: '#1e293b' }}>{rep.title}</td>
                  <td style={{ padding: '16px 20px' }}><span style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>{rep.category}</span></td>
                  <td style={{ padding: '16px 20px', color: '#64748b' }}>{rep.date}</td>
                  <td style={{ padding: '16px 20px', fontWeight: '600', color: '#059669' }}>{rep.format}</td>
                  <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                    <button onClick={() => showToast(`Downloading ${rep.title}...`, 'success')} style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#2563eb', padding: '6px 14px', borderRadius: '8px', fontWeight: '600', fontSize: '12px', cursor: 'pointer' }}>
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderAnalyticsModule = () => {
    return (
      <div className="unidash-fade-in" style={{ padding: '20px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Institutional Analytics Dashboard</h2>
            <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '14px' }}>Comprehensive metrics across student attendance, placement rates, and branch metrics.</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '28px' }}>
          <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Overall Attendance Rate</div>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#16a34a', margin: '8px 0 4px' }}>91.4%</div>
            <div style={{ fontSize: '12px', color: '#16a34a' }}>↑ 2.3% higher than last semester</div>
          </div>
          <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Placement Eligibility Index</div>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#2563eb', margin: '8px 0 4px' }}>86.2%</div>
            <div style={{ fontSize: '12px', color: '#2563eb' }}>412 placement-ready students</div>
          </div>
          <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Active Industry Engagements</div>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#9333ea', margin: '8px 0 4px' }}>14</div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Professors of Practice scheduled</div>
          </div>
          <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Top Performing Division</div>
            <div style={{ fontSize: '20px', fontWeight: '800', color: '#1e293b', margin: '12px 0 6px' }}>Computer Science</div>
            <div style={{ fontSize: '12px', color: '#d97706' }}>94% placement rate</div>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '0 0 16px 0' }}>Monthly Session & Attendance Trends</h3>
          <div style={{ height: '280px', width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { month: "Feb", sessions: 8, attendance: 88 },
                { month: "Mar", sessions: 12, attendance: 90 },
                { month: "Apr", sessions: 15, attendance: 92 },
                { month: "May", sessions: 11, attendance: 89 },
                { month: "Jun", sessions: 14, attendance: 93 },
                { month: "Jul", sessions: 16, attendance: 91 }
              ]}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="attendance" fill="#3b82f6" name="Attendance %" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const renderDynamicRoleDashboard = () => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const roleName = storedUser?.role_name || uniProfile?.role_name || (uniProfile?.role !== 'custom_role' && uniProfile?.role !== 'role' && uniProfile?.role) || (storedUser?.role !== 'custom_role' && storedUser?.role !== 'role' && storedUser?.role) || 'Custom Role';
    const nonDashModules = accessibleModules.filter(m => m.key !== 'dashboard');

    return (
      <div className="unidash-fade-in" style={{ padding: '10px 0' }}>
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '16px',
          padding: '28px 32px',
          color: '#0f172a',
          border: '1px solid #e2e8f0',
          marginBottom: '32px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            </div>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '800', letterSpacing: '-0.5px', color: '#0f172a' }}>
              Welcome, {uniProfile?.name || storedUser?.name || 'Authorized User'}
            </h1>
            <p style={{ margin: '6px 0 0', color: '#475569', fontSize: '15px' }}>
              You are signed in as <strong style={{ color: '#2563eb' }}>{roleName}</strong>. Your dashboard reflects your assigned permissions and accessible modules below.
            </p>
          </div>

        </div>

        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
            Accessible Modules & Quick Actions
          </h2>
          <span style={{ fontSize: '13px', color: '#64748b' }}>
            Click any widget to launch module management
          </span>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '24px',
          marginBottom: '40px'
        }}>
          {nonDashModules.map((mod) => {
            let badgeText = "Module Ready";
            let badgeBg = "#eff6ff";
            let badgeColor = "#2563eb";
            let statsSummary = "";
            let actionText = `Manage ${mod.label}`;
            let iconComponent = <LayoutDashboard size={24} />;

            if (mod.key === 'students') {
              badgeText = "Student Directory";
              badgeBg = "#f0fdf4"; badgeColor = "#16a34a";
              statsSummary = "Access active student profiles, academic records, batch allocations, and placement statuses.";
              actionText = "Open Student Management →";
              iconComponent = <Users size={24} style={{ color: '#16a34a' }} />;
            } else if (mod.key === 'faculty') {
              badgeText = "Faculty Directory";
              badgeBg = "#fef3c7"; badgeColor = "#d97706";
              statsSummary = "Manage professor profiles, department designations, course loads, and industry engagements.";
              actionText = "Open Faculty Directory →";
              iconComponent = <UserCheck size={24} style={{ color: '#d97706' }} />;

            } else if (mod.key === 'tpo') {
              badgeText = "Placement Officers";
              badgeBg = "#e0f2fe"; badgeColor = "#0284c7";
              statsSummary = "Coordinate with Training & Placement Officers, campus coordinators, and hiring drives.";
              actionText = "View Placement Officers →";
              iconComponent = <Briefcase size={24} style={{ color: '#0284c7' }} />;
            } else if (mod.key === 'companies') {
              badgeText = "Corporate Partners";
              badgeBg = "#ffedd5"; badgeColor = "#ea580c";
              statsSummary = "Review registered companies, open job profiles, corporate eligibility criteria, and hiring pipelines.";
              actionText = "Manage Corporate Partners →";
              iconComponent = <Building2 size={24} style={{ color: '#ea580c' }} />;
            } else if (mod.key === 'placement_drives') {
              badgeText = "Recruitment Schedule";
              badgeBg = "#fee2e2"; badgeColor = "#dc2626";
              statsSummary = "Schedule placement drives, coordinate interview rounds, and manage corporate campus visits.";
              actionText = "Schedule & View Drives →";
              iconComponent = <Calendar size={24} style={{ color: '#dc2626' }} />;
            } else if (mod.key === 'analytics') {
              badgeText = "Performance Insights";
              badgeBg = "#f1f5f9"; badgeColor = "#475569";
              statsSummary = "Examine real-time placement percentages, student attendance rates, and institutional indices.";
              actionText = "Explore Analytics →";
              iconComponent = <BarChart3 size={24} style={{ color: '#475569' }} />;
            } else if (mod.key === 'reports') {
              badgeText = "Official Reports";
              badgeBg = "#ecfdf5"; badgeColor = "#059669";
              statsSummary = "Generate, review, and export academic, placement, and departmental compliance reports.";
              actionText = "Access Reports Center →";
              iconComponent = <FileText size={24} style={{ color: '#059669' }} />;
            } else if (mod.key === 'courses' || mod.key === 'lms') {
              badgeText = "Curriculum & LMS";
              badgeBg = "#eff6ff"; badgeColor = "#3b82f6";
              statsSummary = "Configure university courses, curriculum modules, and credit assignment structures.";
              actionText = "Open LMS Center →";
              iconComponent = <BookOpen size={24} style={{ color: '#3b82f6' }} />;
            } else if (mod.key === 'roles') {
              badgeText = "Security & RBAC";
              badgeBg = "#faf5ff"; badgeColor = "#7e22ce";
              statsSummary = "Create custom roles, configure granular permissions, and oversee institutional authorization.";
              actionText = "Manage Roles →";
              iconComponent = <Shield size={24} style={{ color: '#7e22ce' }} />;
            } else {
              statsSummary = mod.description || "Access role-authorized institutional data and management controls.";
            }

            return (
              <div
                key={mod.key}
                onClick={() => setActiveTab(mod.key)}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 20px -5px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.borderColor = '#94a3b8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '14px',
                      background: badgeBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {iconComponent}
                    </div>
                    <span style={{
                      background: badgeBg,
                      color: badgeColor,
                      fontSize: '11px',
                      fontWeight: '700',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      letterSpacing: '0.3px'
                    }}>
                      {badgeText}
                    </span>
                  </div>
                  <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: '0 0 10px 0' }}>
                    {mod.label}
                  </h3>
                  <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6', margin: '0 0 20px 0' }}>
                    {statsSummary}
                  </p>
                </div>

                <div style={{
                  paddingTop: '16px',
                  borderTop: '1px solid #f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: badgeColor }}>
                    {actionText}
                  </span>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#f8fafc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#64748b'
                  }}>
                    →
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // --- RENDER DASHBOARD ---
  const renderDashboardHome = () => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const storedRole = (uniProfile?.role || storedUser.role || localStorage.getItem('role') || 'university').toLowerCase();
    const isCustomRoleUser = storedRole !== 'university' && storedRole !== 'admin' && uniProfile?.role !== 'university' && (storedUser.role_id || storedUser.base_role === 'custom_role' || storedUser.base_role === 'role' || (accessibleModules && accessibleModules.length > 0 && accessibleModules.length < 12));

    if (isCustomRoleUser && accessibleModules && accessibleModules.length > 0) {
      return renderDynamicRoleDashboard();
    }

    const openReqs = myRequirements.filter(r => r.status === 'OPEN' || r.status === 'PENDING');

    return (
      <div className="unidash-fade-in">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '32px' }}>

          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', borderLeft: '5px solid #3b82f6', padding: '24px', display: 'flex', flexDirection: 'column', position: 'relative', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', minHeight: '140px' }}>
            <CardInfoTooltip title="LMS Courses" content="Total active LMS courses allocated to your institution." />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flex: 1 }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                  LMS Courses
                </div>
                <div style={{ fontSize: '36px', fontWeight: '800', color: '#111827', lineHeight: '1' }}>
                  {dashboardAnalytics?.lms_courses_count || 0}
                </div>
              </div>
              <div style={{ width: '56px', height: '56px', borderRadius: '14px', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6' }}>
                <BookOpen size={28} />
              </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#10b981' }}>
              <TrendingUp size={16} /> <span>Active this semester</span>
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', borderLeft: '5px solid #10b981', padding: '24px', display: 'flex', flexDirection: 'column', position: 'relative', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', minHeight: '140px' }}>
            <CardInfoTooltip title="Targeted Jobs" content="Total jobs specifically targeted at this university." />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flex: 1 }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                  Targeted Jobs
                </div>
                <div style={{ fontSize: '36px', fontWeight: '800', color: '#111827', lineHeight: '1' }}>
                  {dashboardAnalytics?.targeted_jobs_count || 0}
                </div>
              </div>
              <div style={{ width: '56px', height: '56px', borderRadius: '14px', backgroundColor: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
                <Briefcase size={28} />
              </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#10b981' }}>
              <CheckCircle size={16} /> <span>Available to students</span>
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', borderLeft: '5px solid #8b5cf6', padding: '24px', display: 'flex', flexDirection: 'column', position: 'relative', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', minHeight: '140px' }}>
            <CardInfoTooltip title="LMS Licenses" content="Total LMS licenses allocated." />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flex: 1 }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                  LMS Licenses
                </div>
                <div style={{ fontSize: '36px', fontWeight: '800', color: '#111827', lineHeight: '1' }}>
                  {dashboardAnalytics?.lms_licenses_count || 0}
                </div>
              </div>
              <div style={{ width: '56px', height: '56px', borderRadius: '14px', backgroundColor: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8b5cf6' }}>
                <Shield size={28} />
              </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#8b5cf6' }}>
              <Code size={16} /> <span>Active allocations</span>
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', borderLeft: '5px solid #f59e0b', padding: '24px', display: 'flex', flexDirection: 'column', position: 'relative', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', minHeight: '140px' }}>
            <CardInfoTooltip title="AI Token Usage" content="Pooled AI token usage for your institution (Faculty & TPO combined). Contact admin to increase the limit." />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flex: 1 }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                  AI Tokens Used
                </div>
                <div style={{ fontSize: '36px', fontWeight: '800', color: '#111827', lineHeight: '1' }}>
                  {(dashboardAnalytics?.ai_tokens_used || 0).toLocaleString()}
                </div>
                {dashboardAnalytics?.ai_tokens_limit > 0 && (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                    Remaining: <span style={{ fontWeight: '700', color: (dashboardAnalytics?.ai_tokens_remaining || 0) > 10000 ? '#10b981' : '#ef4444' }}>
                      {(dashboardAnalytics?.ai_tokens_remaining || 0).toLocaleString()}
                    </span> / {(dashboardAnalytics?.ai_tokens_limit || 0).toLocaleString()}
                  </div>
                )}
              </div>
              <div style={{ width: '56px', height: '56px', borderRadius: '14px', backgroundColor: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b' }}>
                <Zap size={28} />
              </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#f59e0b' }}>
              <Zap size={16} /> <span>Institution-wide AI usage</span>
            </div>
          </div>

        </div>

        {dashboardAnalytics && (
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '32px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#111827', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Skill Gap Analysis</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Comparing industry demand (Targeted Jobs) vs student supply (AI Skillsets).</p>
              </div>
              <div style={{ display: 'flex', gap: '16px', fontSize: '13px', fontWeight: '600', color: '#4b5563' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#3b82f6' }}></div> Industry Demand</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#10b981' }}></div> Student Supply</span>
              </div>
            </div>

            {dashboardAnalytics.skill_gap_analysis && dashboardAnalytics.skill_gap_analysis.length > 0 ? (
              <div style={{ height: '400px', width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart cx="50%" cy="50%" outerRadius="75%" data={dashboardAnalytics.skill_gap_analysis}>
                    <PolarGrid stroke="#e5e7eb" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: '#4b5563', fontSize: 13, fontWeight: 600 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                    <Radar name="Industry Demand" dataKey="Demand" stroke="#3b82f6" strokeWidth={3} fill="#3b82f6" fillOpacity={0.2} />
                    <Radar name="Student Supply" dataKey="Supply" stroke="#10b981" strokeWidth={3} fill="#10b981" fillOpacity={0.4} />
                    <Tooltip
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                      itemStyle={{ fontWeight: 600 }}
                      formatter={(value) => `${value}%`}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
                Not enough data yet to perform a skill gap analysis.
              </div>
            )}
          </div>
        )}

        {dashboardAnalytics && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginTop: '24px', marginBottom: '24px' }}>
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--border)', padding: '20px', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-color)', fontWeight: 600 }}>Students by Batch</h3>
                  {sortedStudentBatches.length > 0 && (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                      {totalStudentsInBatches} students across {sortedStudentBatches.length} batch{sortedStudentBatches.length === 1 ? '' : 'es'}
                    </div>
                  )}
                </div>
                {sortedStudentBatches.length > 0 && (
                  <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: '8px', padding: '3px', gap: '2px' }}>
                    <button
                      type="button"
                      onClick={() => setStudentsBatchChartType('bar')}
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        fontWeight: 600,
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: studentsBatchChartType === 'bar' ? '#fff' : 'transparent',
                        color: studentsBatchChartType === 'bar' ? '#4f46e5' : '#64748b',
                        boxShadow: studentsBatchChartType === 'bar' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        transition: 'all 0.15s ease'
                      }}
                      title="View as Bar Chart"
                    >
                      <BarChart3 size={13} /> Bar
                    </button>
                    <button
                      type="button"
                      onClick={() => setStudentsBatchChartType('donut')}
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        fontWeight: 600,
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: studentsBatchChartType === 'donut' ? '#fff' : 'transparent',
                        color: studentsBatchChartType === 'donut' ? '#4f46e5' : '#64748b',
                        boxShadow: studentsBatchChartType === 'donut' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        transition: 'all 0.15s ease'
                      }}
                      title="View as Donut Chart"
                    >
                      <PieChartIcon size={13} /> Donut
                    </button>
                  </div>
                )}
              </div>

              {sortedStudentBatches.length > 0 ? (
                studentsBatchChartType === 'bar' ? (
                  <div style={{ height: '300px', width: '100%', minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={sortedStudentBatches} margin={{ top: 12, right: 12, left: -10, bottom: sortedStudentBatches.length > 5 ? 42 : 10 }}>
                        <defs>
                          <linearGradient id="colorStudentsBatch" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.9} />
                            <stop offset="95%" stopColor="#818cf8" stopOpacity={0.4} />
                          </linearGradient>
                        </defs>
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 11, fill: '#64748b' }}
                          angle={sortedStudentBatches.length > 5 ? -30 : 0}
                          textAnchor={sortedStudentBatches.length > 5 ? 'end' : 'middle'}
                          interval={0}
                          height={sortedStudentBatches.length > 5 ? 44 : 25}
                          tickFormatter={(val) => String(val || '')}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                        <Tooltip
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                          formatter={(value) => [
                            `${value} Student${Number(value) === 1 ? '' : 's'} (${totalStudentsInBatches > 0 ? Math.round((Number(value) / totalStudentsInBatches) * 100) : 0}%)`,
                            'Students'
                          ]}
                        />
                        <Bar
                          dataKey="value"
                          fill="url(#colorStudentsBatch)"
                          maxBarSize={45}
                          name="Students"
                          radius={[6, 6, 0, 0]}
                          isAnimationActive={false}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div style={{ height: '300px', width: '100%', minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={donutStudentBatches}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="46%"
                          innerRadius={55}
                          outerRadius={90}
                          paddingAngle={2}
                          isAnimationActive={false}
                        >
                          {donutStudentBatches.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={BATCH_DONUT_COLORS[index % BATCH_DONUT_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                          formatter={(value) => [
                            `${value} Student${Number(value) === 1 ? '' : 's'} (${totalStudentsInBatches > 0 ? Math.round((Number(value) / totalStudentsInBatches) * 100) : 0}%)`,
                            'Count'
                          ]}
                        />
                        <Legend
                          wrapperStyle={{ maxHeight: '68px', overflowY: 'auto', fontSize: '11px', paddingTop: '6px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  No student batch data available yet.
                </div>
              )}
            </div>

            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--border)', padding: '20px', minWidth: 0 }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: 'var(--text-color)', fontWeight: 600 }}>Faculty by Department</h3>
              {dashboardAnalytics.faculty_by_department && dashboardAnalytics.faculty_by_department.length > 0 ? (
                <div style={{ height: '300px', width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={dashboardAnalytics.faculty_by_department}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="46%"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={2}
                        isAnimationActive={false}
                      >
                        {dashboardAnalytics.faculty_by_department.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#8884d8', '#82ca9d', '#ffc658', '#06b6d4', '#a4de6c'][index % 5]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                        formatter={(val) => [`${val} Faculty`, 'Members']}
                      />
                      <Legend wrapperStyle={{ maxHeight: '68px', overflowY: 'auto', fontSize: '11px', paddingTop: '6px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  No faculty department data available yet.
                </div>
              )}
            </div>
          </div>
        )}

        {dashboardAnalytics && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '24px' }}>
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--border)', padding: '20px', minWidth: 0 }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: 'var(--text-color)', fontWeight: 600 }}>Courses Assigned by Batch</h3>
              {dashboardAnalytics.courses_assigned_to_batches && dashboardAnalytics.courses_assigned_to_batches.length > 0 ? (
                <div style={{ height: '300px', width: '100%', minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dashboardAnalytics.courses_assigned_to_batches}
                      margin={{ top: 12, right: 12, left: -10, bottom: dashboardAnalytics.courses_assigned_to_batches.length > 4 ? 38 : 10 }}
                    >
                      <defs>
                        <linearGradient id="colorCourses" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.9} />
                          <stop offset="95%" stopColor="#60a5fa" stopOpacity={0.4} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        angle={dashboardAnalytics.courses_assigned_to_batches.length > 4 ? -25 : 0}
                        textAnchor={dashboardAnalytics.courses_assigned_to_batches.length > 4 ? 'end' : 'middle'}
                        interval={0}
                        height={dashboardAnalytics.courses_assigned_to_batches.length > 4 ? 40 : 25}
                        tickFormatter={(val) => val.length > 18 ? val.substring(0, 18) + '...' : val}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Tooltip
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                        formatter={(val) => [`${val} Course${Number(val) === 1 ? '' : 's'} Assigned`, 'Courses']}
                      />
                      <Bar dataKey="value" fill="url(#colorCourses)" maxBarSize={50} name="Courses" radius={[6, 6, 0, 0]} isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  No course assignment data available yet.
                </div>
              )}
            </div>

            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--border)', padding: '20px', minWidth: 0 }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: 'var(--text-color)', fontWeight: 600 }}>AI Usage Minutes by Batch</h3>
              {dashboardAnalytics.ai_usage_by_batch && dashboardAnalytics.ai_usage_by_batch.length > 0 ? (
                <div style={{ height: '300px', width: '100%', minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dashboardAnalytics.ai_usage_by_batch}
                      margin={{ top: 12, right: 12, left: -5, bottom: dashboardAnalytics.ai_usage_by_batch.length > 4 ? 38 : 10 }}
                    >
                      <defs>
                        <linearGradient id="colorAI" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.9} />
                          <stop offset="95%" stopColor="#34d399" stopOpacity={0.4} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        angle={dashboardAnalytics.ai_usage_by_batch.length > 4 ? -25 : 0}
                        textAnchor={dashboardAnalytics.ai_usage_by_batch.length > 4 ? 'end' : 'middle'}
                        interval={0}
                        height={dashboardAnalytics.ai_usage_by_batch.length > 4 ? 40 : 25}
                        tickFormatter={(val) => val.length > 18 ? val.substring(0, 18) + '...' : val}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Tooltip
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                        formatter={(val) => [`${Number(val).toLocaleString()} Minutes`, 'Usage']}
                      />
                      <Bar dataKey="value" fill="url(#colorAI)" maxBarSize={50} name="Minutes" radius={[6, 6, 0, 0]} isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  No AI usage data available yet.
                </div>
              )}
            </div>
          </div>
        )}

        {false && (
        <div className="unidash-list-section" style={{ position: 'relative' }}>
          <div className="action-bar">
            <h2>Active Recruitment Listings</h2>
            <button id="post-requirement-button" className="uni-btn-primary" onClick={() => { resetForm(); setShowPostModal(true); }}>
              <PlusCircle size={18} /> <span>Post New Requirement</span>
            </button>
          </div>
          <CardInfoTooltip title="Active Recruitment Listings" content="Manage your ongoing opportunities. Click on a listing to see matched experts." />

          <div className="uni-requirements-list">
            {openReqs.length === 0 ? (
              <div className="empty-state">No active recruitment postings found.</div>
            ) : (
              openReqs.map((req) => {
                const isSelected = selectedReqId === req?.id;
                return (
                  <div key={req?.id} className={`uni-req-item ${isSelected ? 'active-border' : ''}`}>
                    <div className="uni-req-header">
                      <div className="uni-req-info">
                        <strong style={{ color: '#1f2937', fontSize: '18px' }}>{req?.title}</strong>
                        <div style={{ display: 'flex', gap: '8px', margin: '4px 0' }}>
                          <span style={{
                            fontSize: '10px',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            background: 'var(--border)',
                            color: 'var(--text-muted)',
                            fontWeight: '700',
                            textTransform: 'uppercase'
                          }}>
                            {req.engagement_type}
                          </span>
                        </div>
                        <p>
                          {req?.required_domain} • {req?.delivery_mode}
                          {(req?.delivery_mode === 'In-Person' || req?.delivery_mode === 'Hybrid') && req?.location ? ` (${req?.location})` : ''}
                        </p>
                        <div className="req-schedule-row" style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-muted)' }}>
                            <Calendar size={14} />
                            {req.event_date ? (
                              <>
                                {new Date(req.event_date).toLocaleDateString('en-GB')}
                                {req.engagement_type !== 'VISITING' && req.end_date && ` - ${new Date(req.end_date).toLocaleDateString('en-GB')}`}
                              </>
                            ) : 'Date TBD'}
                          </span>

                          {/* Visiting specifics */}
                          {req.engagement_type === 'VISITING' && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-muted)' }}>
                              <Clock size={14} />
                              {req.start_time || 'Time TBD'}
                              {req.end_time && ` - ${req.end_time}`}
                              {` (${req.duration_value} ${req.duration_unit})`}
                            </span>
                          )}

                          {/* Modular/Adjunct/Fractional specifics */}
                          {(['MODULAR', 'ADJUNCT', 'FRACTIONAL'].includes(req.engagement_type)) && (
                            <>
                              {req.start_time && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-muted)' }}>
                                  <Clock size={14} /> {req.start_time}{req.end_time ? ` - ${req.end_time}` : ''}
                                </span>
                              )}
                              {req.duration_unit !== 'Hours' && req.engagement_type !== 'ADJUNCT' && req.duration_value && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-muted)' }}>
                                  {req.engagement_type === 'MODULAR' ? <Zap size={14} /> : <Briefcase size={14} />}
                                  {req.duration_value} {req.duration_unit === 'Months' ? `${req.duration_value} Months` : req.duration_unit}
                                </span>
                              )}
                              {req.engagement_type === 'FRACTIONAL' && req.weekly_hours > 0 && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-muted)' }}>
                                  <Clock size={14} /> {req.weekly_hours}hr/week Quota
                                </span>
                              )}
                              {req.days_of_week && req.days_of_week.length > 0 && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-muted)' }}>
                                  <Calendar size={14} /> {req.days_of_week.map(d => d.substring(0, 3)).join(', ')}
                                </span>
                              )}
                            </>
                          )}

                          {/* Fallback for others if any */}
                          {(!['VISITING', 'MODULAR', 'ADJUNCT', 'FRACTIONAL'].includes(req.engagement_type)) && req.start_time && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-muted)' }}>
                              <Clock size={14} /> {req.start_time}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="uni-req-actions">
                        <button className="uni-btn-match" onClick={() => viewMatches(req?.id)}>
                          {isSelected ? 'Hide Panel' : 'View Expert Matches'}
                        </button>
                        <button className="uni-btn-edit-icon" onClick={() => handleEditClick(req)}><Pencil size={18} /></button>
                        <button className="uni-btn-delete-icon" onClick={() => handleDelete(req?.id)}><Trash2 size={18} /></button>
                      </div>
                    </div>

                    {isSelected && (
                      <div className="uni-matches-container unidash-fade-in">
                        <div className="match-panel-header" style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)', fontWeight: '600' }}>
                          <Users size={18} /> <span>Recommended Industry Experts</span>
                        </div>
                        {loadingMatches ? (
                          <div className="loading-spinner">Analyzing Experts...</div>
                        ) : (
                          <div className="professors-list">
                            {matches.length === 0 ? (
                              <p className="no-matches">No professors found matching your criteria.</p>
                            ) : (
                              matches.map(pop => (
                                <div key={pop.id} className={`uni-match-card ${pop.invitation_status === 'PROPOSED_BY_POP' ? 'highlight-proposal' : ''}`}>
                                  <div className="uni-match-pop-info">
                                    <div className="pop-name-row">
                                      <h3>{pop.full_name}</h3>
                                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                        <span className="seniority-tag">{pop.years_of_experience} Years Exp.</span>
                                        <span style={{
                                          background: pop.years_of_experience >= 10 ? '#f0fdf4' : '#eff6ff',
                                          color: pop.years_of_experience >= 10 ? '#166534' : '#1e40af',
                                          padding: '2px 8px',
                                          borderRadius: '12px',
                                          fontSize: '10px',
                                          fontWeight: '700',
                                          border: `1px solid ${pop.years_of_experience >= 10 ? '#bbf7d0' : '#bfdbfe'}`,
                                          textTransform: 'uppercase'
                                        }}>
                                          {pop.years_of_experience >= 10 ? 'Professor of Practice' : 'Instructor'}
                                        </span>
                                      </div>
                                    </div>
                                    <p className="pop-subtext"><Briefcase size={14} /> {pop.job_title} @ {pop.company}</p>

                                    {/* Estimated Price Display */}
                                    <div style={{
                                      marginTop: '10px',
                                      padding: '8px 12px',
                                      background: 'linear-gradient(to right, #faf5ff, #f3e8ff)',
                                      borderRadius: '8px',
                                      border: '1px solid #e9d5ff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between'
                                    }}>
                                      <div>
                                        <span style={{ fontSize: '11px', color: '#7c3aed', fontWeight: '500' }}>Session Budget</span>
                                        <div style={{ fontSize: '10px', color: '#a78bfa' }}>
                                          Allocated Amount
                                        </div>
                                      </div>
                                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#5b21b6' }}>
                                        ₹{req.budget ? req.budget.toLocaleString() : 0}
                                      </div>
                                    </div>

                                    {pop.linkedin_url && (
                                      <a
                                        href={/^https?:\/\//i.test(pop.linkedin_url) ? pop.linkedin_url : `https://${pop.linkedin_url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="pop-linkedin-link"
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          fontSize: '12px',
                                          color: '#0077b5',
                                          textDecoration: 'none',
                                          marginTop: '8px',
                                          fontWeight: '600'
                                        }}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#0077b5"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
                                        View LinkedIn Profile
                                      </a>
                                    )}

                                    {/* Polymorphic Availability Info */}
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                                      {pop.weekly_quota_hours > 0 && (
                                        <div style={{ background: 'var(--cyan-dim)', color: 'var(--cyan)', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          <Clock size={12} /> {pop.weekly_quota_hours} hr/week Quota
                                        </div>
                                      )}
                                      {(pop.window_start_date || pop.window_end_date) && (
                                        <div style={{ background: '#fef2f2', color: '#991b1b', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          <Calendar size={12} />
                                          {pop.window_start_date ? new Date(pop.window_start_date).toLocaleDateString('en-GB') : 'Now'} - {pop.window_end_date ? new Date(pop.window_end_date).toLocaleDateString('en-GB') : '∞'}
                                        </div>
                                      )}
                                    </div>

                                  </div>
                                  <div className="uni-match-action">
                                    {pop.invitation_status === 'PROPOSED_BY_POP' || pop.invitation_status === 'REQUESTED' ? (
                                      <div className="proposal-review-actions">
                                        <span className="proposal-badge">{pop.invitation_status === 'REQUESTED' ? 'Waiting for Professor' : 'Application Received'}</span>
                                        {pop.invitation_status === 'PROPOSED_BY_POP' && (
                                          <div className="pop-table-actions">
                                            <button className="pop-action-decline" onClick={() => handleEngagementResponse(pop.engagement_id, 'DECLINED')}><XCircle size={20} /></button>
                                            <button className="pop-action-accept" onClick={() => handleEngagementResponse(pop.engagement_id, 'ACCEPTED')}><CheckCircle size={20} /></button>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="status-container">
                                        {pop.invitation_status ? (
                                          <span className={`uni-status-badge status-${pop.invitation_status.toLowerCase()}`}>
                                            {pop.invitation_status}
                                          </span>
                                        ) : (
                                          <button className="uni-btn-invite" onClick={() => handleSendRequest(selectedReqId, pop.id)}>
                                            <UserPlus size={16} /> Send Invitation
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>
        )}
      </div>
    )
  };



  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const storedRole = (uniProfile?.role || storedUser.role || localStorage.getItem('role') || 'university').toLowerCase();
  const isCustomRoleUser = storedRole !== 'university' && storedRole !== 'admin' && uniProfile?.role !== 'university' && (storedUser.role_id || storedUser.base_role === 'custom_role' || storedUser.base_role === 'role' || (accessibleModules && accessibleModules.length > 0 && accessibleModules.length < 12));

  return (
    <div className="dashboard-wrapper">
      {/* Mobile sidebar overlay */}
      {isSidebarOpen && (
        <div
          className="uni-sidebar-overlay"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside className={`sidebar${isSidebarOpen ? ' sidebar-open' : ''}`}>
        <button
          className="uni-sidebar-close"
          onClick={() => setIsSidebarOpen(false)}
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
        <nav className="sidebar-nav">
          {isCustomRoleUser && accessibleModules && accessibleModules.length > 0 ? (
            accessibleModules.map(mod => {
              const isActive = activeTab === mod.key;
              let iconEl = <LayoutDashboard size={20} />;
              if (mod.key === 'students') iconEl = <UserPlus size={20} />;
              else if (mod.key === 'faculty') iconEl = <Users size={20} />;

              else if (mod.key === 'tpo') iconEl = <Briefcase size={20} />;
              else if (mod.key === 'companies') iconEl = <Building2 size={20} />;
              else if (mod.key === 'placement_drives') iconEl = <Calendar size={20} />;
              else if (mod.key === 'analytics') iconEl = <BarChart3 size={20} />;
              else if (mod.key === 'reports') iconEl = <FileText size={20} />;
              else if (mod.key === 'courses' || mod.key === 'lms') iconEl = <BookOpen size={20} />;
              else if (mod.key === 'discussions') iconEl = <Users size={20} />;
              else if (mod.key === 'stage_allocation') iconEl = <Layers size={20} />;
              else if (mod.key === 'ai_reports') iconEl = <Brain size={20} />;

              const navBtn = (
                <button
                  key={mod.key}
                  className={isActive ? 'active' : ''}
                  onClick={() => navTo(mod.key)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                >
                  {iconEl} <span>{mod.label}</span>
                </button>
              );

              if (mod.key === 'ai_reports') {
                return (
                  <FeatureGate key={mod.key} feature="ai_student_reports">
                    {navBtn}
                  </FeatureGate>
                );
              }
              if (mod.key === 'stage_allocation') {
                return (
                  <FeatureGate key={mod.key} feature="sidebar_interviewer">
                    {navBtn}
                  </FeatureGate>
                );
              }
              return navBtn;
            })
          ) : (
            <>
              <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => navTo('dashboard')}>
                <LayoutDashboard size={20} /> Dashboard
              </button>
              {/* <button id="university-engagements-tab" className={activeTab === 'schedule' ? 'active' : ''} onClick={() => navTo('schedule')}>
                <Calendar size={20} /> My Schedule
              </button> */}
              <button className={activeTab === 'placement_drives' ? 'active' : ''} onClick={() => navTo('placement_drives')}>
                <Calendar size={20} /> Placement Drives
              </button>
              <FeatureGate feature="ai_student_reports">
                <button className={activeTab === 'ai_reports' ? 'active' : ''} onClick={() => navTo('ai_reports')}>
                  <Brain size={20} /> AI Student Reports
                </button>
              </FeatureGate>
              <FeatureGate feature="sidebar_interviewer">
                <button
                  id="university-stage-allocation-tab"
                  className={activeTab === 'stage_allocation' ? 'active' : ''}
                  onClick={() => navTo('stage_allocation')}
                >
                  <Layers size={20} /> AI Interview Tiers
                </button>
              </FeatureGate>
              {/* <button className={activeTab === 'completed' ? 'active' : ''} onClick={() => setActiveTab('completed')}>
                <CheckCircle size={20} /> Completed Sessions
              </button> */}
              {/* <button className={activeTab === 'discover' ? 'active' : ''} onClick={() => setActiveTab('discover')}>
                <Search size={20} /> Discover Experts
              </button> */}
              {/* User Management Section */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <button
                  className={['students', 'faculty', 'tpo'].includes(activeTab) || (activeTab && activeTab.startsWith('custom_role_')) ? 'active' : ''}
                  onClick={() => setIsUserMgmtOpen(!isUserMgmtOpen)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <UserPlus size={20} /> User Management
                    </div>
                  </div>
                </button>

                {isUserMgmtOpen && (
                  <div style={{ display: 'flex', flexDirection: 'column', marginTop: '2px', paddingLeft: '15px', borderLeft: '2px solid rgba(255,255,255,0.05)', marginLeft: '10px' }}>
                    <button className={activeTab === 'students' ? 'active' : ''} onClick={() => navTo('students')}>
                      <GraduationCap size={18} /> Students
                    </button>
                    <button className={activeTab === 'faculty' ? 'active' : ''} onClick={() => navTo('faculty')}>
                      <UserCheck size={18} /> Faculty
                    </button>
                    <button className={activeTab === 'tpo' ? 'active' : ''} onClick={() => navTo('tpo')}>
                      <Briefcase size={18} /> TPO
                    </button>
                    {customRoles && customRoles.map(role => {
                      const tabKey = `custom_role_${role.id}`;
                      const roleName = role.name || role.role_name || 'Custom Role';
                      return (
                        <button
                          key={role.id}
                          className={activeTab === tabKey ? 'active' : ''}
                          onClick={() => { navTo(tabKey); setSelectedCustomRole(role); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                        >
                          <Shield size={18} /> {roleName}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Deactivated Users Section */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <button
                  className={['deactivated_students', 'deactivated_faculty', 'deactivated_tpo'].includes(activeTab) || (activeTab && activeTab.startsWith('deactivated_custom_role_')) ? 'active' : ''}
                  onClick={() => setIsDeactivatedOpen(!isDeactivatedOpen)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <UserX size={20} /> Deactivated Accounts
                    </div>
                  </div>
                </button>

                {isDeactivatedOpen && (
                  <div style={{ display: 'flex', flexDirection: 'column', marginTop: '2px', paddingLeft: '15px', borderLeft: '2px solid rgba(255,255,255,0.05)', marginLeft: '10px' }}>
                    <button className={activeTab === 'deactivated_students' ? 'active' : ''} onClick={() => navTo('deactivated_students')}>
                      <GraduationCap size={18} /> Students
                    </button>
                    <button className={activeTab === 'deactivated_faculty' ? 'active' : ''} onClick={() => navTo('deactivated_faculty')}>
                      <UserCheck size={18} /> Faculty
                    </button>
                    <button className={activeTab === 'deactivated_tpo' ? 'active' : ''} onClick={() => navTo('deactivated_tpo')}>
                      <Briefcase size={18} /> TPO
                    </button>
                    {customRoles && customRoles.map(role => {
                      const tabKey = `deactivated_custom_role_${role.id}`;
                      const roleName = role.name || role.role_name || 'Custom Role';
                      return (
                        <button
                          key={role.id}
                          className={activeTab === tabKey ? 'active' : ''}
                          onClick={() => { navTo(tabKey); setSelectedCustomRole(role); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                        >
                          <Shield size={18} /> {roleName}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <FeatureGate feature="messages">
<button className={activeTab === 'chat' ? 'active' : ''} onClick={() => navTo('chat')}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <MessageSquare size={20} /> Messages
                  </div>
                  {unreadMessagesCount > 0 && (
                    <span style={{
                      background: '#ef4444',
                      color: 'white',
                      borderRadius: '50%',
                      padding: '2px 8px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      minWidth: '20px',
                      textAlign: 'center'
                    }}>
                      {unreadMessagesCount}
                    </span>
                  )}
                </div>
              </button>
</FeatureGate>

              <FeatureGate feature="discussions">
              <SubFeatureGate feature="discussions" sub="groups_view">
              <button
                onClick={() => navTo('discussions')}
                className={activeTab === 'discussions' || location.pathname === '/discussions' ? 'active' : ''}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Users size={20} /> Groups
                </div>
              </button>
              </SubFeatureGate>
              </FeatureGate>


              <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => navTo('settings')}>
                <User size={20} /> Settings
              </button>

              <FeatureGate feature="lms">
              <button
                onClick={() => navTo('lms')}
                className={activeTab === 'lms' ? 'active nav-item-lms' : 'nav-item-lms'}
                style={activeTab !== 'lms' ? { display: 'flex', alignItems: 'center', gap: '10px', color: '#3b82f6', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' } : { display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Briefcase size={20} /> LMS & Curriculum
                </div>
              </button>
              </FeatureGate>
            </>
          )}
        </nav>

      </aside>
      <main className="main-content">
        <header className="top-bar">
          <div className="top-bar-left">
            <button
              className="uni-hamburger"
              onClick={() => setIsSidebarOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
            <h1>
            {activeTab === 'discussions' ? 'Groups' :
              activeTab === 'chat' ? 'Messages & Chat' :
                activeTab === 'dashboard' ? (isCustomRoleUser ? 'Dashboard' : 'University Analytics') :
                  activeTab === 'schedule' ? 'Confirmed Schedule & Drives' :
                    activeTab === 'placement_drives' ? 'Placement Drives' :
                      activeTab === 'ai_reports' ? 'AI Student Reports' :
                        activeTab === 'stage_allocation' ? 'AI Interview Tiers' :
                          activeTab === 'completed' ? 'Completed Sessions' :
                        activeTab === 'students' ? 'Student Management' :
                          activeTab === 'faculty' ? 'Faculty Management' :

                            activeTab === 'companies' ? 'Corporate Partners & Companies' :
                              activeTab === 'reports' ? 'Official Reports & Compliance Center' :
                                activeTab === 'analytics' ? 'Institutional Analytics' :
                                  activeTab === 'tpo' ? 'TPO Management' :
                                    (activeTab && activeTab.startsWith('custom_role_')) ? `${(customRoles.find(r => `custom_role_${r.id}` === activeTab) || selectedCustomRole)?.name || 'Custom Role'} Management` :
                                      activeTab === 'settings' ? 'Settings' :
                                        activeTab === 'lms' || activeTab === 'courses' ? 'LMS Curriculum' :
                                          activeTab === 'applied_candidates_view' ? 'Applied Candidates' :
                                            'Expert Discovery'}
            </h1>
          </div>
          <div className="top-bar-right">
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <button
                onClick={() => setShowNotifs(!showNotifs)}
                title="Notifications"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', position: 'relative', marginRight: '16px' }}
              >
                <Bell size={22} />
                {notifications.some(n => !n.is_read) && (
                  <span style={{
                    position: 'absolute', top: '-2px', right: '-2px',
                    width: '10px', height: '10px', background: '#ef4444',
                    borderRadius: '50%', border: '2px solid white'
                  }}></span>
                )}
              </button>
              {showNotifs && (
                <div className="uni-card" style={{
                  position: 'absolute', top: '50px', right: 0, width: '320px',
                  zIndex: 100, padding: '16px', border: '1px solid var(--border)',
                  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', background: 'var(--graphite-dark)', borderRadius: '12px', textAlign: 'left'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: 600, margin: 0, color: 'var(--proto-white)' }}>Notifications</h4>
                      {notifications.some(n => !n.is_read) && (
                        <button onClick={handleMarkAllAsRead} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', fontSize: '11px', color: 'var(--cyan)' }}>
                          Mark all as read
                        </button>
                      )}
                    </div>
                    <button onClick={() => setShowNotifs(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                      <XCircle size={16} color="var(--text-muted)" />
                    </button>
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {notifications.filter(n => !n.is_read).length > 0 ? notifications.filter(n => !n.is_read).map(n => (
                      <div key={n.id} style={{ padding: '10px', borderBottom: '1px solid var(--border)', fontSize: '13px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', background: 'rgba(59, 130, 246, 0.05)' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: 'var(--proto-white)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444', display: 'inline-block', flexShrink: 0 }}></span>
                            {n.title}
                          </div>
                          <div style={{ color: 'var(--text-muted)', marginTop: '2px' }}>{n.message}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>{new Date(n.created_at).toLocaleString()}</div>
                        </div>
                        <button onClick={() => handleMarkAsRead(n.id)} title="Mark as read" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-muted)' }}>
                          <CheckCircle size={14} />
                        </button>
                      </div>
                    )) : <p style={{ fontSize: '13px', color: 'var(--text-faint)', textAlign: 'center', padding: '10px' }}>No new notifications</p>}
                  </div>
                </div>
              )}
            </div>

            <button
              className={`icon-btn-top ${showCalendarModal ? 'active' : ''}`}
              onClick={() => setShowCalendarModal(true)}
              title="Monthly Calendar View"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
            >
              <Calendar size={22} />
            </button>
          </div>
        </header>
        {!isCustomRoleUser && <OnboardingTour user={uniProfile} role="university" />}
        <section className={`content-body ${activeTab === 'chat' || activeTab === 'discussions' ? 'full-width-body' : ''}`}>
          {activeTab === 'discussions' ? <FeatureGate feature="discussions"><DiscussionsPage embedded={true} /></FeatureGate> :
            activeTab === 'chat' ? <FeatureGate feature="messages"><ChatPage embedded={true} /></FeatureGate> :
              activeTab === 'dashboard' ? renderDashboardHome() :
                activeTab === 'schedule' ? renderSchedule() :
                  activeTab === 'placement_drives' ? (isCustomRoleUser ? <PlacementDrivesModule showToast={showToast} onViewCandidates={(company, role) => { setSelectedCandidateCompany(company); setSelectedCandidateRole(role); setActiveTab('applied_candidates_view'); }} /> : <UniPlacementOverview />) :
                    activeTab === 'applied_candidates_view' ? <AppliedCandidatesContainer initialCompany={selectedCandidateCompany} initialRole={selectedCandidateRole} onBack={() => setActiveTab('placement_drives')} /> :
                      activeTab === 'ai_reports' ? (
                        <FeatureGate
                          feature="ai_student_reports"
                          fallback={<div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>AI Student Reports feature is not enabled for your institution.</div>}
                        >
                          <UniAiReports showToast={showToast} />
                        </FeatureGate>
                      ) :
                        activeTab === 'stage_allocation' ? (
                          <FeatureGate feature="sidebar_interviewer" fallback={<div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>AI Interviewer feature is not enabled for your institution.</div>}>
                            <StageAllocation showToast={showToast} readOnly={true} />
                          </FeatureGate>
                        ) :
                          activeTab === 'completed' ? renderCompletedSessions() :
                        activeTab === 'students' ? renderStudentsSection(false) :
                          activeTab === 'deactivated_students' ? renderStudentsSection(true) :
                            activeTab === 'faculty' ? <FacultyManagement isDeactivatedView={false} /> :
                              activeTab === 'deactivated_faculty' ? <FacultyManagement isDeactivatedView={true} /> :
                                activeTab === 'companies' ? renderCompaniesModule() :
                                  activeTab === 'reports' ? renderReportsModule() :
                                    activeTab === 'analytics' ? renderAnalyticsModule() :
                                      activeTab === 'tpo' ? <TpoManagement isDeactivatedView={false} /> :
                                        activeTab === 'deactivated_tpo' ? <TpoManagement isDeactivatedView={true} /> :
                                          (activeTab && activeTab.startsWith('custom_role_')) ? (
                                            <CustomRoleManagementPage
                                              role={customRoles.find(r => `custom_role_${r.id}` === activeTab) || selectedCustomRole}
                                              onRefreshRoles={fetchCustomRoles}
                                              isDeactivatedView={false}
                                            />
                                          ) :
                                            (activeTab && activeTab.startsWith('deactivated_custom_role_')) ? (
                                              <CustomRoleManagementPage
                                                role={customRoles.find(r => `deactivated_custom_role_${r.id}` === activeTab) || selectedCustomRole}
                                                onRefreshRoles={fetchCustomRoles}
                                                isDeactivatedView={true}
                                              />
                                            ) :
                                              activeTab === 'settings' ? renderMySettings() :
                                                activeTab === 'lms' || activeTab === 'courses' ? <PartnerCourses /> :
                                                  <DiscoverProfessorsUni />}
        </section>
      </main>

      {showPostModal && (
        <div className="uni-modal-overlay">
          <div className="uni-modal-full-content">
            <header className="uni-modal-header-full">
              <div className="modal-header-inner">
                <h3>{editingReqId ? 'Edit Opportunity' : 'Create Opportunity'}</h3>
                <button className="close-x" onClick={() => { setShowPostModal(false); resetForm(); }}>&times;</button>
              </div>
            </header>
            <div className="modal-body-full">
              <form onSubmit={handleCreate} className="full-page-form">
                <div className="form-grid">

                  {/* SECTION 1: OPPORTUNITY PROFILE */}
                  <div className="form-section">
                    <div className="form-section-title">Opportunity Profile</div>
                    <div className="form-row">
                      <div className="uni-input-group" style={{ flex: 1 }}>
                        <label>Requirement Title</label>
                        <input required list="course-list" placeholder="e.g., AI in Healthcare Guest Lecture" value={newReq.title} onChange={(e) => {
                          const val = e.target.value;
                          const matchedCourse = lmsCourses.find(c => c.title === val);
                          setNewReq({
                            ...newReq,
                            title: val,
                            description: matchedCourse ? matchedCourse.description : newReq.description,
                            course_id: matchedCourse ? matchedCourse.externalContentRootId : ''
                          });
                        }} />
                        <datalist id="course-list">
                          {lmsCourses.map(course => (
                            <option key={`req-${course.externalContentRootId}`} value={course.title} />
                          ))}
                        </datalist>
                      </div>

                      <div className="uni-input-group" style={{ flex: 1 }}>
                        <label>Academic Batch (For LMS)</label>
                        {newReq.course_id && uniAssignedCourses.find(c => c.externalContentRootId === newReq.course_id)?.assigned_batches?.length > 0 ? (
                          <select
                            value={newReq.academic_batch}
                            onChange={(e) => setNewReq({ ...newReq, academic_batch: e.target.value })}
                            required
                          >
                            <option value="">Select a batch...</option>
                            {uniAssignedCourses.find(c => c.externalContentRootId === newReq.course_id).assigned_batches.map(batch => (
                              <option key={batch} value={batch}>{batch}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            placeholder={newReq.course_id ? "No batches assigned to this course yet" : "Select a course first..."}
                            value={newReq.academic_batch}
                            onChange={(e) => setNewReq({ ...newReq, academic_batch: e.target.value })}
                            disabled={true}
                          />
                        )}
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="uni-input-group full-width">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                          <FileText size={16} color="#6366f1" /> Description of the session to be taken
                        </label>
                        <div style={{ position: 'relative' }}>
                          <textarea
                            rows="4"
                            placeholder="Provide full details of what the session entails, objectives, prerequisites, and expected outcomes..."
                            value={newReq.description}
                            onChange={(e) => setNewReq({ ...newReq, description: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              borderRadius: '8px',
                              border: '1px solid var(--border)',
                              fontSize: '14px',
                              color: 'var(--proto-white)',
                              lineHeight: '1.6',
                              resize: 'vertical',
                              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
                              transition: 'all 0.2s ease',
                              fontFamily: 'inherit'
                            }}
                          />
                        </div>
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px', display: 'block' }}>A detailed description helps professors understand your requirements better and improves match quality.</span>
                      </div>
                    </div>
                    <div className="form-row two-col">
                      <div className="uni-input-group">
                        <label>Required Domain</label>
                        <select
                          required
                          value={newReq.required_domain}
                          onChange={(e) => setNewReq({ ...newReq, required_domain: e.target.value })}
                        >
                          {INDUSTRY_DOMAINS.map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                      </div>
                      <div className="uni-input-group">
                        <label>Engagement Type</label>
                        <select
                          value={newReq.engagement_type}
                          onChange={(e) => {
                            const newType = e.target.value;
                            let updatedUnit = newReq.duration_unit;
                            if (['ADJUNCT', 'FRACTIONAL'].includes(newType) && updatedUnit === 'Hours') {
                              updatedUnit = 'Sessions';
                            }
                            setNewReq({ ...newReq, engagement_type: newType, duration_unit: updatedUnit });
                          }}
                        >
                          <option value="VISITING">Visiting Faculty</option>
                          <option value="ADJUNCT">Adjunct Faculty</option>
                          <option value="FRACTIONAL">Fractional PoP</option>
                          <option value="MODULAR">Modular PoP</option>
                        </select>
                      </div>
                    </div>
                    {/* Rate Card Display */}
                    <div style={{ marginTop: '16px', padding: '12px', background: 'var(--engine-black)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)' }}>Standardized Rate ({uniProfile?.tier || 'Unclassified'})</span>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--proto-white)' }}>
                          ₹{(() => {
                            if (!uniProfile?.tier) return 0;
                            // Robust lookup handling potential whitespace
                            const tierKey = uniProfile.tier.trim();
                            const rate = RATE_CARD[newReq.engagement_type]?.[tierKey];
                            return rate || 0;
                          })()}/hr
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Estimated Budget</span>
                        <span style={{ fontSize: '16px', fontWeight: '800', color: '#2563eb' }}>
                          ₹{(() => {
                            const rateInfo = RATE_CARD[newReq.engagement_type];
                            const currentTier = uniProfile?.tier?.trim();
                            const hourlyRate = (rateInfo && currentTier) ? (rateInfo[currentTier] || 0) : 0;
                            const totalHours = calculateTotalHours(newReq);
                            return Math.round(hourlyRate * totalHours).toLocaleString();
                          })()}
                          <span style={{ fontSize: '11px', fontWeight: 'normal', color: 'var(--text-muted)', marginLeft: '4px' }}>
                            total
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* SECTION 2: DELIVERY & LOGISTICS */}
                  <div className="form-section">
                    <div className="form-section-title">Delivery & Logistics</div>
                    <div className="form-row two-col">
                      <div className="uni-input-group">
                        <label>Delivery Mode</label>
                        <select value={newReq.delivery_mode} onChange={(e) => setNewReq({ ...newReq, delivery_mode: e.target.value })}>
                          <option value="Remote">Remote</option>
                          <option value="In-Person">In-Person</option>
                          <option value="Hybrid">Hybrid</option>
                        </select>
                      </div>
                      {(newReq.delivery_mode === 'In-Person' || newReq.delivery_mode === 'Hybrid') && (
                        <div className="uni-input-group">
                          <label>Location Details *</label>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                            <input placeholder="City" value={newReq.city || ''} onChange={(e) => setNewReq({ ...newReq, city: e.target.value })} required />
                            <input placeholder="State" value={newReq.state || ''} onChange={(e) => setNewReq({ ...newReq, state: e.target.value })} required />
                            <input placeholder="Country" value={newReq.country || ''} onChange={(e) => setNewReq({ ...newReq, country: e.target.value })} required />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* SECTION 3: SCHEDULE & COMMITMENT */}
                  <div className="form-section">
                    <div className="form-section-title">Schedule & Commitment</div>

                    {newReq.engagement_type === 'VISITING' ? (
                      <>
                        <div className="form-row two-col">
                          <div className="uni-input-group">
                            <label>Preferred Date</label>
                            <input type="date" required value={newReq.event_date} onChange={(e) => setNewReq({ ...newReq, event_date: e.target.value })} />
                          </div>
                          <div className="uni-input-group">
                            <label>Time (24h)</label>
                            <input
                              type="time"
                              required
                              value={newReq.start_time}
                              onChange={(e) => setNewReq({ ...newReq, start_time: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="uni-input-group full-width">
                            <label>Duration (Hours)</label>
                            <div className="duration-input-wrapper">
                              <div className="duration-field" style={{ flex: 1 }}>
                                <input
                                  type="number"
                                  min="0.5"
                                  step="0.5"
                                  value={newReq.duration_value}
                                  onChange={(e) => setNewReq({ ...newReq, duration_value: parseFloat(e.target.value) || 0, duration_unit: 'Hours' })}
                                />
                              </div>
                              <div className="duration-field" style={{ flex: 2, display: 'flex', alignItems: 'center', paddingLeft: '12px', color: 'var(--text-muted)', fontWeight: '500' }}>
                                Hours
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : newReq.engagement_type === 'FRACTIONAL' ? (
                      <>
                        <div className="form-row two-col">
                          <div className="uni-input-group">
                            <label>Start Date</label>
                            <input type="date" required value={newReq.event_date} min={new Date().toISOString().split('T')[0]} onChange={(e) => setNewReq({ ...newReq, event_date: e.target.value })} />
                          </div>
                          <div className="uni-input-group">
                            <label>End Date</label>
                            <input type="date" required value={newReq.end_date} min={newReq.event_date || new Date().toISOString().split('T')[0]} onChange={(e) => setNewReq({ ...newReq, end_date: e.target.value })} />
                          </div>
                        </div>
                        <div className="form-row two-col">
                          <div className="uni-input-group">
                            <label>Weekly Hours Quota</label>
                            <input
                              type="number"
                              required
                              placeholder="e.g. 10"
                              value={newReq.weekly_hours}
                              onChange={(e) => setNewReq({ ...newReq, weekly_hours: parseFloat(e.target.value) || 0 })}
                            />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="uni-input-group full-width">
                            <label>Preferred Days (Optional)</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                <button
                                  key={day}
                                  type="button"
                                  onClick={() => handleDayToggle(day)}
                                  style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: newReq.days_of_week?.includes(day) ? '2px solid #3b82f6' : '1px solid var(--border)',
                                    background: newReq.days_of_week?.includes(day) ? '#eff6ff' : 'white',
                                    color: newReq.days_of_week?.includes(day) ? '#3b82f6' : 'var(--text-muted)',
                                    fontWeight: newReq.days_of_week?.includes(day) ? '600' : '400',
                                    cursor: 'pointer',
                                    fontSize: '13px'
                                  }}
                                >
                                  {day.substring(0, 3)}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : newReq.engagement_type === 'MODULAR' ? (
                      <>
                        <div className="form-row two-col">
                          <div className="uni-input-group">
                            <label>Module Start Date</label>
                            <input type="date" required value={newReq.event_date} min={new Date().toISOString().split('T')[0]} onChange={(e) => setNewReq({ ...newReq, event_date: e.target.value })} />
                          </div>
                          <div className="uni-input-group">
                            <label>Module End Date</label>
                            <input type="date" required value={newReq.end_date} min={newReq.event_date || new Date().toISOString().split('T')[0]} onChange={(e) => setNewReq({ ...newReq, end_date: e.target.value })} />
                          </div>
                        </div>
                        <div className="form-row two-col">
                          <div className="uni-input-group">
                            <label>Session Timing</label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <input
                                type="time"
                                required
                                value={newReq.start_time}
                                onChange={(e) => setNewReq({ ...newReq, start_time: e.target.value })}
                              />
                              <input
                                type="time"
                                required
                                value={newReq.end_time}
                                onChange={(e) => setNewReq({ ...newReq, end_time: e.target.value })}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      // ADJUNCT
                      <>
                        <div className="form-row two-col">
                          <div className="uni-input-group">
                            <label>Start Date</label>
                            <input type="date" required value={newReq.event_date} min={new Date().toISOString().split('T')[0]} onChange={(e) => setNewReq({ ...newReq, event_date: e.target.value })} />
                          </div>
                          <div className="uni-input-group">
                            <label>End Date</label>
                            <input type="date" required value={newReq.end_date} min={newReq.event_date || new Date().toISOString().split('T')[0]} onChange={(e) => setNewReq({ ...newReq, end_date: e.target.value })} />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="uni-input-group full-width">
                            <label>Scheduled Days *</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                <button
                                  key={day}
                                  type="button"
                                  onClick={() => handleDayToggle(day)}
                                  style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: newReq.days_of_week?.includes(day) ? '2px solid #3b82f6' : '1px solid var(--border)',
                                    background: newReq.days_of_week?.includes(day) ? '#eff6ff' : 'white',
                                    color: newReq.days_of_week?.includes(day) ? '#3b82f6' : 'var(--text-muted)',
                                    fontWeight: newReq.days_of_week?.includes(day) ? '600' : '400',
                                    cursor: 'pointer',
                                    fontSize: '13px'
                                  }}
                                >
                                  {day.substring(0, 3)}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="form-row two-col">
                          <div className="uni-input-group">
                            <label>Session Timing</label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <input
                                type="time"
                                required
                                value={newReq.start_time}
                                onChange={(e) => setNewReq({ ...newReq, start_time: e.target.value })}
                              />
                              <input
                                type="time"
                                required
                                value={newReq.end_time}
                                onChange={(e) => setNewReq({ ...newReq, end_time: e.target.value })}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>


                </div>
                <div className="full-modal-footer">
                  <button type="button" className="btn-cancel-large" onClick={() => { setShowPostModal(false); resetForm(); }}>Cancel</button>
                  <button type="submit" className="uni-btn-primary-large">{editingReqId ? 'Update Posting' : 'Publish Opportunity'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showPaymentModal && paymentData && (
        <PaymentModal
          session={paymentData.session}
          sessionType={paymentData.sessionType}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentData(null);
          }}
          onPaymentComplete={() => {
            setShowPaymentModal(false);
            setPaymentData(null);
            fetchRequirements(); // Refresh
          }}
        />
      )}
      {showCalendarModal && (
        <div className="uni-modal-overlay-cal" onClick={() => setShowCalendarModal(false)}>
          {/* This container is now constrained to 75% width */}
          <div className="uni-modal-3qtr pop-fade-up" onClick={e => e.stopPropagation()}>
            <header className="uni-modal-header-3qtr">
              <div className="modal-header-inner">
                <div className="title-with-icon">
                  <Calendar size={20} className="text-indigo" />
                  <h3>Monthly Engagement Schedule</h3>
                </div>
                <button className="close-x" onClick={() => setShowCalendarModal(false)}>&times;</button>
              </div>
            </header>

            <div className="calendar-split-layout">
              {/* Left: Interactive Calendar */}
              <div className="cal-picker-side">
                <CalendarLib
                  onChange={setSelectedDate}
                  value={selectedDate}
                  tileContent={({ date, view }) =>
                    view === 'month' && getSessionsForDate(date).length > 0
                      ? <div className="dot-indicator"></div> : null
                  }
                />
              </div>

              {/* Right: Focused Agenda */}
              <div className="cal-agenda-side">
                <div className="agenda-header-sticky">
                  <h4>{selectedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</h4>
                  <span className="count-pill">{getSessionsForDate(selectedDate).length} Sessions</span>
                </div>

                <div className="agenda-scroll-area">
                  {getSessionsForDate(selectedDate).length > 0 ? (
                    getSessionsForDate(selectedDate).map(session => (
                      <div key={session.id} className="agenda-item-3qtr">
                        <div className="time-col">{session.start_time}</div>
                        <div className="info-col">
                          <p className="title">{session.title}</p>
                          <p className="expert">Expert: {session.professor_name || 'Accepted'}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="agenda-empty-state">
                      <Clock size={32} />
                      <p>No confirmed engagements</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

      )}

      {/* Feedback Modal - Modern Premium Design */}
      {showFeedbackModal && (
        <div className="uni-modal-overlay" onClick={() => setShowFeedbackModal(false)} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div className="uni-modal-full-content" style={{
            maxWidth: '520px',
            width: '90%',
            borderRadius: '12px',
            overflow: 'hidden',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            margin: '0 auto',
            background: 'var(--graphite-dark)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Header with Gradient */}
            <header className="uni-modal-header-full" style={{
              background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
              padding: '24px',
              position: 'relative',
              border: 'none'
            }}>
              <div className="modal-header-inner" style={{ position: 'relative' }}>
                <h3 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '6px', color: 'white' }}>Share Your Experience</h3>
                <p style={{ fontSize: '13px', opacity: 0.9, color: 'white', margin: 0 }}>Your feedback helps us improve</p>
                <button
                  className="close-x"
                  onClick={() => setShowFeedbackModal(false)}
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    background: 'rgba(255, 255, 255, 0.15)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontSize: '24px',
                    color: 'white'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.25)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.15)'}
                >×</button>
              </div>
            </header>

            {/* Body */}
            <div className="modal-body-full" style={{
              padding: '24px',
              background: 'var(--engine-black)',
              overflowY: 'auto',
              flex: 1
            }}>
              {/* Quality Rating */}
              <div style={{
                marginBottom: '20px',
                padding: '18px',
                background: 'var(--graphite-dark)',
                borderRadius: '10px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid var(--border)'
              }}>
                <label style={{
                  display: 'block',
                  fontWeight: 600,
                  marginBottom: '10px',
                  color: 'var(--proto-white)',
                  fontSize: '14px'
                }}>
                  Session Quality <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>Rate the overall quality</p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      onClick={() => setFeedbackData({ ...feedbackData, quality_rating: rating })}
                      onMouseEnter={(e) => {
                        if (rating > feedbackData.quality_rating) {
                          e.target.style.transform = 'scale(1.15)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                      }}
                      style={{
                        fontSize: '32px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: rating <= feedbackData.quality_rating ? '#f59e0b' : '#e5e7eb',
                        transition: 'all 0.2s ease',
                        transform: 'scale(1)',
                        filter: rating <= feedbackData.quality_rating ? 'drop-shadow(0 2px 4px rgba(245, 158, 11, 0.3))' : 'none'
                      }}
                    >
                      ★
                    </button>
                  ))}
                </div>
                {feedbackData.quality_rating > 0 && (
                  <p style={{
                    textAlign: 'center',
                    marginTop: '12px',
                    fontSize: '12px',
                    color: 'var(--proto-white)',
                    fontWeight: 600
                  }}>
                    {feedbackData.quality_rating === 5 ? '🎉 Excellent!' :
                      feedbackData.quality_rating === 4 ? '👍 Great!' :
                        feedbackData.quality_rating === 3 ? '👌 Good' :
                          feedbackData.quality_rating === 2 ? '😐 Fair' : '😞 Needs Improvement'}
                  </p>
                )}
              </div>

              {/* Expert Effectiveness */}
              <div style={{
                marginBottom: '28px',
                padding: '24px',
                background: 'var(--graphite-dark)',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                border: '1px solid #e5e7eb'
              }}>
                <label style={{
                  display: 'block',
                  fontWeight: 700,
                  marginBottom: '12px',
                  color: 'var(--proto-white)',
                  fontSize: '15px'
                }}>
                  <span style={{ fontSize: '18px', marginRight: '8px' }}>👨‍🏫</span>
                  Expert Effectiveness
                </label>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>How effective was the expert in delivering the content?</p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      onClick={() => setFeedbackData({ ...feedbackData, expert_effectiveness: rating })}
                      onMouseEnter={(e) => {
                        if (rating > feedbackData.expert_effectiveness) {
                          e.target.style.transform = 'scale(1.2)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                      }}
                      style={{
                        fontSize: '40px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: rating <= feedbackData.expert_effectiveness ? '#fbbf24' : '#e5e7eb',
                        transition: 'all 0.2s ease',
                        transform: 'scale(1)',
                        filter: rating <= feedbackData.expert_effectiveness ? 'drop-shadow(0 2px 4px rgba(251, 191, 36, 0.3))' : 'none'
                      }}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>

              {/* Relevance Rating */}
              <div style={{
                marginBottom: '28px',
                padding: '24px',
                background: 'var(--graphite-dark)',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                border: '1px solid #e5e7eb'
              }}>
                <label style={{
                  display: 'block',
                  fontWeight: 700,
                  marginBottom: '12px',
                  color: 'var(--proto-white)',
                  fontSize: '15px'
                }}>
                  {/* <span style={{ fontSize: '18px', marginRight: '8px' }}></span> */}
                  Relevance of Guidance
                </label>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>How relevant was the content to your learning goals?</p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      onClick={() => setFeedbackData({ ...feedbackData, relevance_rating: rating })}
                      onMouseEnter={(e) => {
                        if (rating > feedbackData.relevance_rating) {
                          e.target.style.transform = 'scale(1.2)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                      }}
                      style={{
                        fontSize: '40px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: rating <= feedbackData.relevance_rating ? '#fbbf24' : '#e5e7eb',
                        transition: 'all 0.2s ease',
                        transform: 'scale(1)',
                        filter: rating <= feedbackData.relevance_rating ? 'drop-shadow(0 2px 4px rgba(251, 191, 36, 0.3))' : 'none'
                      }}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>

              {/* Comments */}
              <div style={{
                padding: '24px',
                background: 'var(--graphite-dark)',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                border: '1px solid #e5e7eb'
              }}>
                <label style={{
                  display: 'block',
                  fontWeight: 700,
                  marginBottom: '12px',
                  color: 'var(--proto-white)',
                  fontSize: '15px'
                }}>
                  {/* <span style={{ fontSize: '18px', marginRight: '8px' }}>💭</span> */}
                  Additional Comments
                  <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--text-faint)', marginLeft: '4px' }}>(Optional)</span>
                </label>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>Share any specific feedback, suggestions, or highlights from the session</p>
                <textarea
                  value={feedbackData.comments}
                  onChange={(e) => setFeedbackData({ ...feedbackData, comments: e.target.value })}
                  placeholder="What did you like most? What could be improved? Any specific insights you gained?"
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '14px',
                    borderRadius: '10px',
                    border: '2px solid var(--border)',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    transition: 'border-color 0.2s',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="full-modal-footer" style={{
              padding: '20px 24px',
              background: 'var(--graphite-dark)',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                className="btn-cancel-large"
                onClick={() => setShowFeedbackModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--graphite-dark)',
                  color: 'var(--text-muted)',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = 'var(--border)';
                  e.target.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = 'var(--border)';
                  e.target.style.background = 'white';
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="uni-btn-primary-large"
                onClick={submitFeedback}
                disabled={feedbackData.quality_rating === 0}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: feedbackData.quality_rating === 0
                    ? 'var(--border)'
                    : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: feedbackData.quality_rating === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: feedbackData.quality_rating === 0
                    ? 'none'
                    : '0 2px 8px rgba(59, 130, 246, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (feedbackData.quality_rating > 0) {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (feedbackData.quality_rating > 0) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
                  }
                }}
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Students List Modal */}
      {showStudentsModal && (
        <div className="uni-modal-overlay" onClick={() => setShowStudentsModal(false)}>
          <div className="uni-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '750px', minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
            <header className="uni-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ background: '#e0f2fe', color: '#0369a1', padding: '8px', borderRadius: '10px' }}>
                  <Users size={20} />
                </div>
                <div>
                  <h3>Enrolled Students</h3>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>Roster for "{currentReqTitle}"</p>
                </div>
              </div>
              <button className="close-x" onClick={() => setShowStudentsModal(false)}>&times;</button>
            </header>
            <div className="uni-modal-body" style={{ padding: '0px', flex: 1, maxHeight: 'none' }}>
              {currentStudentsList.length === 0 ? (
                <div className="empty-state" style={{ padding: '60px 40px', textAlign: 'center' }}>
                  <Users size={48} style={{ color: 'var(--border)', marginBottom: '16px' }} />
                  <p style={{ color: 'var(--text-muted)', fontWeight: '500' }}>No students enrolled yet.</p>
                  <small style={{ color: 'var(--text-faint)' }}>Student matching for this session is currently in progress.</small>
                </div>
              ) : (
                <div style={{ padding: '12px' }}>
                  {currentStudentsList.map((student, idx) => (
                    <div key={idx} style={{
                      padding: '16px',
                      marginBottom: '8px',
                      background: 'var(--graphite-dark)',
                      border: '1px solid var(--border)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'all 0.2s ease'
                    }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'white'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{
                          width: '40px', height: '40px',
                          background: 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)',
                          color: 'white',
                          borderRadius: '12px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: '700', fontSize: '15px',
                          boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.2)'
                        }}>
                          {student.full_name.charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', color: 'var(--proto-white)', fontSize: '15px' }}>{student.full_name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <GraduationCap size={14} />
                              {student.degree} • Class of {student.graduation_year}
                            </span>
                            <span style={{ color: 'var(--border)' }}>|</span>
                            <span>{student.branch || 'No Branch'}</span>
                            <span style={{ color: 'var(--border)' }}>|</span>
                            <span>USN: {student.usn || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                      <div style={{
                        fontSize: '11px',
                        background: 'var(--cyan-dim)',
                        color: 'var(--cyan)',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontWeight: '700',
                        border: '1px solid #dcfce7',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <CheckCircle size={12} />
                        ENROLLED
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Student Upload Modal - Full Page */}
      {showUploadModal && (
        <div className="uni-modal-overlay">
          <div
            className="uni-modal-full-content"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'var(--graphite-dark)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Header */}
            <header style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 40px',
              borderBottom: '1px solid var(--border)',
              background: 'var(--graphite-dark)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Upload size={24} style={{ color: '#3b82f6' }} />
                <div>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: 'var(--proto-white)' }}>
                    {uploadStep === 'select' && 'Upload Student Data'}
                    {uploadStep === 'preview' && 'Preview Upload'}
                    {uploadStep === 'result' && 'Upload Complete'}
                  </h2>
                  <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>
                    {uploadStep === 'select' && 'Import students from Excel or CSV file'}
                    {uploadStep === 'preview' && 'Review the data before creating accounts'}
                    {uploadStep === 'result' && 'Student accounts have been processed'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowUploadModal(false)}
                style={{
                  background: 'none',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: 'var(--text-muted)',
                  fontSize: '14px'
                }}
              >
                <X size={18} /> Close
              </button>
            </header>

            {/* Body - Scrollable */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: '40px',
              background: 'var(--engine-black)'
            }}>
              <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                {/* Step 1: File Selection */}
                {uploadStep === 'select' && (
                  <div>
                    {/* Upload Zone */}
                    <div style={{
                      border: '3px dashed var(--border)',
                      borderRadius: '16px',
                      padding: '80px 40px',
                      textAlign: 'center',
                      background: 'var(--graphite-dark)',
                      marginBottom: '30px'
                    }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        background: 'var(--vector-blue-dim)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px'
                      }}>
                        <Upload size={36} style={{ color: '#3b82f6' }} />
                      </div>
                      <h3 style={{ fontSize: '18px', color: 'var(--proto-white)', marginBottom: '8px' }}>
                        Drop your Excel file here or click to browse
                      </h3>
                      <p style={{ color: 'var(--text-faint)', fontSize: '14px', marginBottom: '24px' }}>
                        Supports .xlsx and .csv files
                      </p>
                      <input
                        type="file"
                        accept=".xlsx,.csv"
                        onChange={(e) => setUploadFile(e.target.files[0])}
                        style={{ display: 'none' }}
                        id="student-file-input"
                      />
                      <label
                        htmlFor="student-file-input"
                        className="uni-btn-primary"
                        style={{
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '14px 28px',
                          fontSize: '15px'
                        }}
                      >
                        <Upload size={18} /> Select File
                      </label>

                      <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Need the correct format?</span>
                        <button
                          onClick={handleDownloadTemplate}
                          style={{
                            background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px'
                          }}
                        >
                          <FileDown size={14} /> Download Template
                        </button>
                      </div>
                    </div>

                    {/* Selected File */}
                    {uploadFile && (
                      <div style={{
                        padding: '20px',
                        background: 'var(--cyan-dim)',
                        borderRadius: '12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '30px',
                        border: '1px solid var(--border)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <CheckCircle size={24} style={{ color: '#22c55e' }} />
                          <div>
                            <span style={{ color: 'var(--cyan)', fontWeight: '600', fontSize: '15px', display: 'block' }}>
                              {uploadFile.name}
                            </span>
                            <span style={{ color: 'var(--cyan)', fontSize: '13px' }}>
                              {(uploadFile.size / 1024).toFixed(1)} KB
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => setUploadFile(null)}
                          style={{
                            background: 'var(--graphite-dark)',
                            border: '1px solid #fca5a5',
                            borderRadius: '8px',
                            padding: '8px 16px',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}

                    {/* Instructions */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '24px'
                    }}>
                      <div style={{
                        padding: '24px',
                        background: 'var(--graphite-dark)',
                        borderRadius: '12px',
                        border: '1px solid var(--border)'
                      }}>
                        <h4 style={{ fontSize: '15px', fontWeight: '600', color: 'var(--vector-blue)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ background: '#dbeafe', padding: '6px 10px', borderRadius: '6px' }}>Required Columns</span>
                        </h4>
                        <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--text-muted)', fontSize: '14px', lineHeight: '2' }}>
                          <li><strong>Name as SSLC</strong> - Student's Full Name</li>
                          <li><strong>Student Email</strong> - Student email address</li>
                          <li><strong>Student Mobile Number</strong> - Contact number</li>
                          <li><strong>Student Gender</strong> - MALE/FEMALE</li>
                        </ul>
                      </div>
                      <div style={{
                        padding: '24px',
                        background: 'var(--graphite-dark)',
                        borderRadius: '12px',
                        border: '1px solid var(--border)'
                      }}>
                        <h4 style={{ fontSize: '15px', fontWeight: '600', color: 'var(--cyan)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ background: 'var(--cyan-dim)', padding: '6px 10px', borderRadius: '6px' }}>Academic Columns</span>
                        </h4>
                        <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--text-muted)', fontSize: '14px', lineHeight: '2' }}>
                          <li><strong>USN</strong> - University Seat Number</li>
                          <li><strong>Admission Number</strong> - Admission Number</li>
                          <li><strong>Academic Year</strong> - E.g. 2024-25</li>
                          <li><strong>Academic Batch</strong> - E.g. 2024-2028</li>
                          <li><strong>Admission Semester</strong> - E.g. 3</li>
                          <li><strong>Branch</strong> - E.g. CSE</li>
                          <li><strong>Graduation Level</strong> - E.g. UG, PG</li>
                          <li><strong>Program</strong> - e.g. B.Tech, M.Tech, MCA</li>
                          <li><strong>Date of Birth</strong> - E.g. YYYY-MM-DD</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Preview */}
                {uploadStep === 'preview' && uploadPreview && (
                  <div>
                    {/* Summary Stats */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                      <div style={{ padding: '30px', background: 'var(--graphite-dark)', borderRadius: '16px', textAlign: 'center', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '48px', fontWeight: '700', color: 'var(--cyan)' }}>{uploadPreview.valid_rows}</div>
                        <div style={{ fontSize: '14px', color: 'var(--cyan)', fontWeight: '500' }}>Valid Rows</div>
                      </div>
                      <div style={{ padding: '30px', background: 'var(--graphite-dark)', borderRadius: '16px', textAlign: 'center', border: '1px solid #fcd34d' }}>
                        <div style={{ fontSize: '48px', fontWeight: '700', color: '#92400e' }}>{uploadPreview.duplicate_emails?.length || 0}</div>
                        <div style={{ fontSize: '14px', color: '#a16207', fontWeight: '500' }}>Duplicates</div>
                      </div>
                      <div style={{ padding: '30px', background: 'var(--graphite-dark)', borderRadius: '16px', textAlign: 'center', border: '1px solid #fca5a5' }}>
                        <div style={{ fontSize: '48px', fontWeight: '700', color: '#991b1b' }}>{uploadPreview.invalid_rows}</div>
                        <div style={{ fontSize: '14px', color: '#b91c1c', fontWeight: '500' }}>Invalid</div>
                      </div>
                    </div>

                    {/* Validation Errors */}
                    {uploadPreview.validation_errors?.length > 0 && (
                      <div style={{ marginBottom: '20px', background: 'var(--graphite-dark)', borderRadius: '12px', border: '1px solid #fca5a5', overflow: 'hidden' }}>
                        <div style={{ padding: '16px 20px', background: '#fef2f2', borderBottom: '1px solid #fca5a5' }}>
                          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#dc2626' }}>
                            Validation Errors ({uploadPreview.validation_errors.length})
                          </h4>
                        </div>
                        <div style={{ maxHeight: '200px', overflow: 'auto', padding: '16px 20px' }}>
                          {uploadPreview.validation_errors.map((err, idx) => (
                            <div key={idx} style={{ fontSize: '13px', color: '#991b1b', padding: '8px 0', borderBottom: idx < uploadPreview.validation_errors.length - 1 ? '1px solid #fee2e2' : 'none' }}>
                              <strong>Row {err.row}:</strong> {err.errors.map(e => e.error).join(', ')}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Notice for skipped invalid rows */}
                    {uploadPreview.invalid_rows > 0 && (
                      <div style={{
                        marginBottom: '25px',
                        padding: '14px 18px',
                        background: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: '10px',
                        color: '#92400e',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <AlertCircle size={18} style={{ color: '#d97706', flexShrink: 0 }} />
                        <span>
                          <strong>Note:</strong> {uploadPreview.invalid_rows} row(s) with invalid name or email format will be skipped and not created. Only the {uploadPreview.valid_rows} valid student(s) will be added.
                        </span>
                      </div>
                    )}

                    {/* Preview Data Table */}
                    {uploadPreview.preview_data?.length > 0 && (
                      <div style={{ background: 'var(--graphite-dark)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden' }}>
                        <div style={{ padding: '16px 20px', background: 'var(--engine-black)', borderBottom: '1px solid var(--border)' }}>
                          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: 'var(--text-muted)' }}>
                            Preview (first 10 valid rows)
                          </h4>
                        </div>
                        <div style={{ overflow: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                              <tr style={{ background: 'var(--engine-black)' }}>
                                <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>Email</th>
                                <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>Full Name</th>
                                <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>USN</th>
                                <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>Branch</th>
                                <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>Grad Level</th>
                                <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>Program</th>
                                <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>DOB</th>
                              </tr>
                            </thead>
                            <tbody>
                              {uploadPreview.preview_data.slice(0, 10).map((row, idx) => (
                                <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                                  <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--proto-white)' }}>{row.email}</td>
                                  <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--proto-white)', fontWeight: '500' }}>{row.full_name}</td>
                                  <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>{row.usn || '-'}</td>
                                  <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>{row.branch || '-'}</td>
                                  <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>{row.graduation_level || '-'}</td>
                                  <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>{row.program || '-'}</td>
                                  <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>{row.dob || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {uploadPreview.preview_data.length > 10 && (
                          <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', textAlign: 'center', background: 'var(--engine-black)' }}>
                            <button
                              className="uni-btn-primary"
                              onClick={() => setShowFullPreviewModal(true)}
                              style={{ padding: '8px 16px', fontSize: '14px' }}
                            >
                              View More ({uploadPreview.preview_data.length} total rows)
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3: Result */}
                {uploadStep === 'result' && uploadResult && (
                  <div>
                    {/* Success Message */}
                    <div style={{ textAlign: 'center', marginBottom: '40px', padding: '60px', background: 'var(--graphite-dark)', borderRadius: '16px', border: '1px solid var(--border)' }}>
                      <div style={{
                        width: '100px',
                        height: '100px',
                        background: 'var(--cyan-dim)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px'
                      }}>
                        <CheckCircle size={50} style={{ color: '#22c55e' }} />
                      </div>
                      <h2 style={{ fontSize: '28px', fontWeight: '700', color: 'var(--proto-white)', marginBottom: '12px' }}>
                        Upload Complete!
                      </h2>
                      <p style={{ color: 'var(--text-muted)', fontSize: '16px' }}>
                        {uploadResult.successfully_created} student accounts have been created successfully.
                      </p>
                    </div>

                    {/* Result Stats */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                      <div style={{ padding: '30px', background: 'var(--graphite-dark)', borderRadius: '16px', textAlign: 'center', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '48px', fontWeight: '700', color: 'var(--cyan)' }}>{uploadResult.successfully_created}</div>
                        <div style={{ fontSize: '14px', color: 'var(--cyan)', fontWeight: '500' }}>Created</div>
                      </div>
                      <div style={{ padding: '30px', background: 'var(--graphite-dark)', borderRadius: '16px', textAlign: 'center', border: '1px solid #fcd34d' }}>
                        <div style={{ fontSize: '48px', fontWeight: '700', color: '#92400e' }}>{uploadResult.skipped_duplicates}</div>
                        <div style={{ fontSize: '14px', color: '#a16207', fontWeight: '500' }}>Skipped</div>
                      </div>
                      <div style={{ padding: '30px', background: 'var(--graphite-dark)', borderRadius: '16px', textAlign: 'center', border: '1px solid #fca5a5' }}>
                        <div style={{ fontSize: '48px', fontWeight: '700', color: '#991b1b' }}>{uploadResult.failed_rows?.length || 0}</div>
                        <div style={{ fontSize: '14px', color: '#b91c1c', fontWeight: '500' }}>Failed</div>
                      </div>
                    </div>

                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <footer style={{
              padding: '20px 40px',
              borderTop: '1px solid var(--border)',
              background: 'var(--graphite-dark)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              {uploadStep === 'select' && (
                <>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    style={{
                      padding: '12px 24px',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      background: 'var(--graphite-dark)',
                      cursor: 'pointer',
                      fontWeight: '500',
                      color: 'var(--text-muted)'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="uni-btn-primary"
                    disabled={!uploadFile || uploading}
                    onClick={handlePreviewUpload}
                    style={{
                      padding: '12px 28px',
                      opacity: (!uploadFile || uploading) ? 0.5 : 1,
                      fontSize: '15px'
                    }}
                  >
                    {uploading ? 'Processing...' : 'Preview Upload'}
                  </button>
                </>
              )}
              {uploadStep === 'preview' && (
                <>
                  <button
                    onClick={() => setUploadStep('select')}
                    style={{
                      padding: '12px 24px',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      background: 'var(--graphite-dark)',
                      cursor: 'pointer',
                      fontWeight: '500',
                      color: 'var(--text-muted)'
                    }}
                  >
                    Back
                  </button>
                  <button
                    className="uni-btn-primary"
                    disabled={uploadPreview.valid_rows === 0 || uploading}
                    onClick={handleCommitUpload}
                    style={{
                      padding: '12px 28px',
                      opacity: (uploadPreview.valid_rows === 0 || uploading) ? 0.5 : 1,
                      fontSize: '15px'
                    }}
                  >
                    {uploading ? 'Creating Accounts...' : `Create ${uploadPreview.valid_rows} Accounts`}
                  </button>
                </>
              )}
              {uploadStep === 'result' && (
                <button
                  className="uni-btn-primary"
                  onClick={() => {
                    setShowUploadModal(false);
                    fetchStudents();
                  }}
                  style={{ padding: '12px 32px', fontSize: '15px' }}
                >
                  Done
                </button>
              )}
            </footer>
          </div>
        </div>
      )}

      {/* Full Preview Modal */}
      {showFullPreviewModal && uploadPreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-card)',
            borderRadius: '24px',
            width: '100%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
            border: '1px solid var(--border)'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px 40px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'var(--graphite-dark)'
            }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'var(--proto-white)', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Users size={28} style={{ color: 'var(--cyan)' }} />
                All Valid Students Preview
              </h2>
              <button
                onClick={() => setShowFullPreviewModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <X size={18} /> Close
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '40px', overflowY: 'auto', background: 'var(--bg-main)' }}>
              <div style={{ background: 'var(--graphite-dark)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', background: 'var(--engine-black)', borderBottom: '1px solid var(--border)' }}>
                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: 'var(--text-muted)' }}>
                    Total: {uploadPreview.preview_data.length} valid rows
                  </h4>
                </div>
                <div style={{ overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ background: 'var(--engine-black)' }}>
                        <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>Email</th>
                        <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>Full Name</th>
                        <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>USN</th>
                        <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>Branch</th>
                        <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>Grad Level</th>
                        <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>Program</th>
                        <th style={{ padding: '14px 20px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-muted)' }}>DOB</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadPreview.preview_data.map((row, idx) => (
                        <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                          <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--proto-white)' }}>{row.email}</td>
                          <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--proto-white)', fontWeight: '500' }}>{row.full_name}</td>
                          <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>{row.usn || '-'}</td>
                          <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>{row.branch || '-'}</td>
                          <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>{row.graduation_level || '-'}</td>
                          <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>{row.program || '-'}</td>
                          <td style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>{row.dob || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'var(--graphite-dark)',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px 28px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Lock size={20} style={{ color: 'white' }} />
                </div>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--proto-white)', margin: 0 }}>
                  Change Password
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'var(--border)'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                <X size={20} style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>
            {/* Toast Notifications */}
            <div style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              zIndex: 10000,
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {toasts.map(toast => (
                <div
                  key={toast.id}
                  style={{
                    background: toast.type === 'success' ? '#10b981' : '#ef4444',
                    color: '#fff',
                    padding: '16px 24px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    minWidth: '300px',
                    maxWidth: '500px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    animation: 'slideIn 0.3s ease-out',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>
                    {toast.type === 'success' ? '\u2713' : '\u2715'}
                  </span>
                  <span style={{ flex: 1 }}>{toast.message}</span>
                  <button
                    onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '0',
                      opacity: 0.8,
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '1'}
                    onMouseLeave={(e) => e.target.style.opacity = '0.8'}
                  >

                  </button>
                </div>
              ))}
            </div>

            <style>{`
              @keyframes slideIn {
                from {
                  transform: translateX(400px);
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}</style>

            {/* Body */}
            <div style={{ padding: '28px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--text-muted)',
                  marginBottom: '8px'
                }}>
                  Current Password *
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Enter your current password"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '500',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--text-muted)',
                  marginBottom: '8px'
                }}>
                  New Password *
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '500',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--text-muted)',
                  marginBottom: '8px'
                }}>
                  Confirm New Password *
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Re-enter new password"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '500',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowChangePasswordModal(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: '2px solid var(--border)',
                    background: 'var(--graphite-dark)',
                    color: 'var(--text-muted)',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                    e.target.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                    e.target.style.background = 'white';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangePassword}
                  className="uni-btn-primary"
                  style={{
                    padding: '12px 32px',
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Lock size={16} />
                  Update Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'var(--graphite-dark)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'modalSlideIn 0.3s ease-out'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '24px'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '32px'
              }}>
                ⚠️
              </div>
              <h2 style={{
                margin: '0 0 8px',
                fontSize: '24px',
                fontWeight: '700',
                color: '#1f2937'
              }}>
                Confirm Action
              </h2>
              <p style={{
                margin: 0,
                color: '#6b7280',
                fontSize: '15px',
                lineHeight: '1.6'
              }}>
                {confirmModal.message}
              </p>
            </div>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: '2px solid var(--border)',
                  background: 'var(--graphite-dark)',
                  color: 'var(--text-muted)',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = 'var(--border)';
                  e.target.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = 'var(--border)';
                  e.target.style.background = 'white';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => confirmModal.onConfirm()}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#ef4444',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                onMouseLeave={(e) => e.target.style.background = '#ef4444'}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Request Modal */}
      {showRefundModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'var(--graphite-dark)',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            animation: 'modalSlideIn 0.3s ease-out'
          }}>
            <div style={{
              padding: '24px 28px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#fff5f5'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ArrowLeft size={20} style={{ color: 'white' }} />
                </div>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--proto-white)', margin: 0 }}>
                  Request Institutional Refund
                </h2>
              </div>
              <button
                onClick={() => setShowRefundModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
              >
                <X size={20} style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            <div style={{ padding: '28px' }}>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '20px', lineHeight: '1.5' }}>
                You are requesting a refund for your institutional engagement. Compliance will review the request based on the reason provided.
              </p>

              <div style={{
                background: 'var(--engine-black)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px',
                border: '1px solid var(--border)',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px'
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-faint)', textTransform: 'uppercase', marginBottom: '4px' }}>Expert</label>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--proto-white)' }}>{refundData.professor_name}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-faint)', textTransform: 'uppercase', marginBottom: '4px' }}>Requirement</label>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--proto-white)' }}>{refundData.session_title}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-faint)', textTransform: 'uppercase', marginBottom: '4px' }}>Amount</label>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#ef4444' }}>₹{refundData.amount.toLocaleString()}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-faint)', textTransform: 'uppercase', marginBottom: '4px' }}>Payment ID</label>
                  <div style={{ fontSize: '13px', fontFamily: 'monospace', color: 'var(--text-muted)' }}>#{refundData.payment_id}</div>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--text-muted)',
                  marginBottom: '8px'
                }}>
                  Reason for Refund (Minimum 20 characters) *
                </label>
                <textarea
                  value={refundData.details}
                  onChange={(e) => setRefundData({ ...refundData, details: e.target.value })}
                  placeholder="Please describe why the institution is requesting a refund..."
                  required
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    outline: 'none',
                    minHeight: '120px',
                    transition: 'border-color 0.2s',
                    resize: 'none'
                  }}
                />
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '6px'
                }}>
                  <span style={{ fontSize: '11px', color: refundData.details.length < 20 ? '#ef4444' : '#10b981' }}>
                    {refundData.details.length} / 20 characters required
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowRefundModal(false)}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: '2px solid var(--border)',
                    background: 'var(--graphite-dark)',
                    color: 'var(--text-muted)',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitRefundRequest}
                  disabled={refundData.details.length < 20}
                  style={{
                    padding: '12px 32px',
                    fontSize: '15px',
                    background: refundData.details.length < 20 ? '#fca5a5' : '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: refundData.details.length < 20 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                  }}
                >
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expert Proposal Modal (Same as DiscoverProfessorsUni) */}
      {showProposalModal && selectedProf && (
        <div className="stu-modal-overlay">
          <div className="unidis-modal-card pop-fade-up">
            <div className="unidis-modal-header" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div className="unidis-modal-avatar" style={{
                width: '64px', height: '64px', borderRadius: '50%', overflow: 'hidden', background: 'var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: '800', color: 'var(--cyan)',
                border: '2px solid var(--border)', flexShrink: 0
              }}>
                {selectedProf.profile_photo ? (
                  <img
                    src={selectedProf.profile_photo.startsWith('http') ? selectedProf.profile_photo : `${BASE_URL}${selectedProf.profile_photo}`}
                    alt={selectedProf.full_name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  selectedProf.full_name?.charAt(0) || 'P'
                )}
              </div>
              <div className="unidis-modal-title-context" style={{ flex: 1 }}>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--proto-white)', margin: 0 }}>Personal University Proposal</h2>
                <p style={{ color: 'var(--text-muted)', margin: '4px 0 0 0' }}>Requesting: Prof. {selectedProf.full_name}</p>
              </div>
              <button className="unidis-close-x" onClick={() => setShowProposalModal(false)}><X size={24} /></button>
            </div>

            <form onSubmit={handleProposeSubmit} className="unidis-modal-content-grid">
              <div className="unidis-modal-form-side">
                <div className="unidis-input-group">
                  <label>Session Title / Topic</label>
                  <input
                    required
                    list="course-list"
                    placeholder="e.g. Executive Guest Lecture on Fintech"
                    value={proposalForm.title}
                    onChange={e => {
                      const val = e.target.value;
                      const matchedCourse = lmsCourses.find(c => c.title === val);
                      setProposalForm({ ...proposalForm, title: val, description: matchedCourse ? matchedCourse.description : proposalForm.description });
                    }}
                  />
                </div>

                <div className="unidis-input-group">
                  <label>Session Details & Schedule Details</label>
                  <textarea
                    rows={3}
                    placeholder="Provide details about what you expect in the session..."
                    value={proposalForm.description}
                    onChange={e => setProposalForm({ ...proposalForm, description: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      fontSize: '14px',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      outline: 'none',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div className="unidis-grid-half">
                  <div className="unidis-input-group">
                    <label>Engagement Type</label>
                    <select value={proposalForm.engagement_type} onChange={e => setProposalForm({ ...proposalForm, engagement_type: e.target.value })}>
                      <option value="MODULAR">Modular</option>
                      <option value="VISITING">Visiting Faculty</option>
                      <option value="ADJUNCT">Adjunct Professor</option>
                      <option value="FRACTIONAL">Fractional/Part-time</option>
                    </select>
                  </div>
                  <div className="unidis-input-group">
                    <label>Delivery Mode</label>
                    <select value={proposalForm.delivery_mode} onChange={e => setProposalForm({ ...proposalForm, delivery_mode: e.target.value })}>
                      <option value="Remote">Remote</option>
                      <option value="In-Person">In-Person</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>
                </div>

                {['ADJUNCT', 'FRACTIONAL', 'MODULAR'].includes(proposalForm.engagement_type) && (
                  <div className="unidis-grid-half">
                    <div className="unidis-input-group">
                      <label>Start Date</label>
                      <input
                        type="date"
                        required
                        value={proposalForm.event_date}
                        min={new Date().toISOString().split('T')[0]}
                        onChange={e => setProposalForm({ ...proposalForm, event_date: e.target.value })}
                      />
                    </div>
                    <div className="unidis-input-group">
                      <label>End Date</label>
                      <input
                        type="date"
                        required
                        value={proposalForm.end_date}
                        min={proposalForm.event_date || new Date().toISOString().split('T')[0]}
                        onChange={e => setProposalForm({ ...proposalForm, end_date: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                {/* ADJUNCT/MODULAR Time */}
                {(proposalForm.engagement_type === 'ADJUNCT' || proposalForm.engagement_type === 'MODULAR') && (
                  <div className="unidis-grid-half">
                    <div className="unidis-input-group">
                      <label>Start Time</label>
                      <input
                        type="time"
                        required
                        value={proposalForm.start_time}
                        onChange={e => setProposalForm({ ...proposalForm, start_time: e.target.value })}
                      />
                    </div>
                    <div className="unidis-input-group">
                      <label>End Time</label>
                      <input
                        type="time"
                        required
                        value={proposalForm.end_time}
                        onChange={e => setProposalForm({ ...proposalForm, end_time: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                {/* ADJUNCT Days */}
                {proposalForm.engagement_type === 'ADJUNCT' && (
                  <div className="unidis-input-group">
                    <label>Required Days of Week</label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                        <label key={day} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', background: 'var(--border)', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={proposalForm.days_of_week.includes(day)}
                            onChange={e => {
                              const newDays = e.target.checked
                                ? [...proposalForm.days_of_week, day]
                                : proposalForm.days_of_week.filter(d => d !== day);
                              setProposalForm({ ...proposalForm, days_of_week: newDays });
                            }}
                          />
                          {day.substring(0, 3)}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Budget */}
                <div style={{ marginTop: '16px', padding: '12px', background: 'var(--engine-black)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)' }}>Rate ({uniProfile?.tier || 'Unclassified'})</span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--proto-white)' }}>
                      ₹{(() => {
                        const tierKey = (uniProfile?.tier || 'Tier 1').trim();
                        return RATE_CARD[proposalForm.engagement_type]?.[tierKey] || 0;
                      })()}/hr
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Estimated Budget</span>
                    <span style={{ fontSize: '16px', fontWeight: '800', color: '#2563eb' }}>
                      ₹{calculateProposalBudget().toLocaleString()}
                    </span>
                  </div>
                </div>

                <button type="submit" className="unidis-submit-proposal-btn" style={{ marginTop: '20px' }}>
                  <Zap size={16} /> Send Personal Proposal
                </button>
              </div>

              <div className="unidis-modal-calendar-side">
                <label className="unidis-calendar-label">Professor's Availability & Selection</label>
                {loadingCalendar ? (
                  <div className="unidis-calendar-loader">Syncing Professor's availability...</div>
                ) : (
                  <div className="unidis-calendar-wrapper">
                    <FullCalendar
                      plugins={[timeGridPlugin, interactionPlugin]}
                      initialView="timeGridWeek"
                      locale={enGbLocale}
                      allDaySlot={false}
                      height="450px"
                      slotMinTime="08:00:00"
                      slotMaxTime="21:00:00"
                      selectable={!['ADJUNCT', 'FRACTIONAL', 'MODULAR'].includes(proposalForm.engagement_type)}
                      selectOverlap={(event) => event.display === 'background'}
                      selectConstraint={availability.length > 0 ? "availableForSession" : undefined}
                      events={calendarEvents}
                      select={(info) => {
                        const diffMs = info.end - info.start;
                        const diffHrs = Math.round(diffMs / (1000 * 60 * 60));
                        setProposalForm({
                          ...proposalForm,
                          event_date: info.startStr.split('T')[0],
                          start_time: info.start.toTimeString().substring(0, 5),
                          end_time: info.end.toTimeString().substring(0, 5),
                          duration_value: diffHrs || 1
                        });
                      }}
                    />
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            style={{
              background: toast.type === 'success' ? '#10b981' : '#ef4444',
              color: '#fff',
              padding: '16px 24px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              minWidth: '300px',
              maxWidth: '500px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              animation: 'slideIn 0.3s ease-out',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <span style={{ fontSize: '20px' }}>
              {toast.type === 'success' ? '\u2713' : '\u2715'}
            </span>
            <span style={{ flex: 1 }}>{toast.message}</span>
            <button
              onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '0',
                opacity: 0.8,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = '0.8'}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes modalSlideIn {
          from {
            transform: translateY(-50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>

      <ConfirmModal
        {...confirmModal}
        onCancel={closeConfirmModal}
      />
    </div>
  );
}



export default UniDashboard;
// Forced rebuild
